import RtcService from '../services/RtcService'
import CallService from '../services/CallService'
import MediaService from '../services/MediaService'

/** Delay before restoring live presence after the last active chat ends (CTS-9831). */
const POST_CHAT_AVAILABILITY_DELAY_MS = 30000

// Coach-specific ChatManager that coordinates between RtcService, CallService, and MediaService
class CoachChatManager {
  constructor() {
    // Prevent multiple instances
    if (CoachChatManager.instance) {
      return CoachChatManager.instance
    }
    CoachChatManager.instance = this

    // console.log('CoachChatManager - Constructor called, initializing services')

    // Initialize services
    this.rtcService = new RtcService()
    this.callService = new CallService()
    this.mediaService = new MediaService()

    // Initialize logging properties
    this.logChatEventPath = null
    this.logChatMissedOpportunityPath = null
    this.buildChatHistoryPath = null
    this.serverTimeOffset = 0
    this.coachChatCandidateInfoPath = null
    this.coachEndChatPath = null
    this.coachUsername = null // Store coach username for chat history

    // Candidate info cache (similar to legacy chat.js pattern)
    this.candidateInfo = {}
    /** Maps easyrtcid -> chat token (username) so we can resolve names after reconnects */
    this.candidateInfoByEasyrtcid = {}

    /** @type {ReturnType<typeof setTimeout>|null} */
    this._postChatAvailabilityTimer = null
    /** @type {number|null} */
    this._postChatCooldownUntil = null

    /*
    console.log('CoachChatManager - Services initialized:', {
      rtcService: this.rtcService,
      callService: this.callService,
      mediaService: this.mediaService,
    })
    */

    // Coach-specific connection state
    // Support up to 2 concurrent text chats (reduced from legacy's 3)
    // Default unavailable until connect/setAvailability — CTS-9831
    this.connectionState = {
      isConnected: false,
      isCoach: true,
      isAvailable: false,
      availability: 'away',
      videoEnabled: false, // Add videoEnabled property
      candidates: [],
      selectedCandidate: null,
      selectedCandidate2: null,
      activeChats: [], // Max 2 concurrent chats
      pendingChats: [],
      chatHistory: [],
      chatRoom: null,
      myEasyrtcid: null,
      messages: [], // Global messages array (will be per-chat in future)
      chatMode: 'text',
      chatStatus: 'disconnected',
      hasLocalVideo: false,
      hasLocalAudio: false,
      hasRemoteVideo: false,
      hasRemoteAudio: false,
      cameraReady: false,
      localStream: null,
      remoteStream: null,
      // Per-chat tracking (similar to legacy chat.js self.chats structure)
      chats: {
        'chat-1': {
          candidateId: '',
          chatMode: '',
          chatHistory: {
            messages: [],
            errors: [],
            start_time: null,
            end_time: null,
            chat_type: null,
            coach_token: null,
            candidate_token: null,
          },
          conversationId: '',
        },
        'chat-2': {
          candidateId: '',
          chatMode: '',
          chatHistory: {
            messages: [],
            errors: [],
            start_time: null,
            end_time: null,
            chat_type: null,
            coach_token: null,
            candidate_token: null,
          },
          conversationId: '',
        },
      },
      availableChat: 1, // Track which chat slot is available (1 or 2)
    }

    // Event listeners
    this.listeners = new Map()

    // Video element refs (set by React components)
    this.videoRefs = {
      selfVideo: null,
      callerVideo: null,
    }

    // Set up cross-manager communication
    this._setupManagerCommunication()

    // Initialize media service
    this._initializeMediaService()
  }

  /**
   * Determine if we can accept an incoming chat and which slot it should use.
   *
   * Rules:
   * - Text chats can use up to two slots (1 and 2).
   * - Audio/video chats use slot 1 in the UI but require both slots to be free: they need
   *   the full coach layout (no concurrent text in slot 2). If either slot is in use, A/V is over capacity.
   * - If any audio/video chat is pending or active, all additional calls are over capacity.
   */
  _getIncomingChatCapacity(chatMode) {
    const mode = chatMode || 'text'

    const activeChats = this.connectionState.activeChats || []
    const pendingChats = this.connectionState.pendingChats || []

    const hasAvChat =
      activeChats.some(
        (chat) => chat.chatMode === 'audio' || chat.chatMode === 'video',
      ) ||
      pendingChats.some(
        (chat) => chat.chatMode === 'audio' || chat.chatMode === 'video',
      )

    const activeTextChats = activeChats.filter(
      (chat) => chat.chatMode === 'text' || !chat.chatMode,
    )
    const pendingTextChats = pendingChats.filter(
      (chat) => chat.chatMode === 'text' || !chat.chatMode,
    )
    const totalTextChats = activeTextChats.length + pendingTextChats.length

    const slot1InUse = [...activeChats, ...pendingChats].some(
      (chat) => (chat.chatSlot || 1) === 1,
    )
    const slot2InUse = [...activeChats, ...pendingChats].some(
      (chat) => chat.chatSlot === 2,
    )

    const capacityState = {
      mode,
      hasAvChat,
      totalTextChats,
      slot1InUse,
      slot2InUse,
      activeCount: activeChats.length,
      pendingCount: pendingChats.length,
    }

    // Audio/video dominance: if any A/V chat exists, everything else is over capacity
    if (hasAvChat) {
      console.log(
        '[CoachChatManager] _getIncomingChatCapacity - over_capacity due to existing A/V chat:',
        capacityState,
      )
      return { status: 'over_capacity', slot: null }
    }

    // Incoming audio/video: need both slots free (A/V cannot share the coach with any other chat)
    if (mode === 'audio' || mode === 'video') {
      if (!slot1InUse && !slot2InUse) {
        const result = { status: 'accept_in_slot_1', slot: 1 }
        console.log(
          '[CoachChatManager] _getIncomingChatCapacity - accepting A/V in slot 1:',
          { capacityState, result },
        )
        return result
      }
      console.log(
        '[CoachChatManager] _getIncomingChatCapacity - over_capacity for A/V (need both slots free):',
        capacityState,
      )
      return { status: 'over_capacity', slot: null }
    }

    // Text chat capacity: up to two slots total
    if (totalTextChats >= 2) {
      console.log(
        '[CoachChatManager] _getIncomingChatCapacity - over_capacity for text, already at 2 text chats:',
        capacityState,
      )
      return { status: 'over_capacity', slot: null }
    }

    // Prefer slot 1, then slot 2
    if (!slot1InUse) {
      const result = { status: 'accept_in_slot_1', slot: 1 }
      console.log(
        '[CoachChatManager] _getIncomingChatCapacity - accepting text in slot 1:',
        { capacityState, result },
      )
      return result
    }
    if (!slot2InUse) {
      const result = { status: 'accept_in_slot_2', slot: 2 }
      console.log(
        '[CoachChatManager] _getIncomingChatCapacity - accepting text in slot 2:',
        { capacityState, result },
      )
      return result
    }

    console.log(
      '[CoachChatManager] _getIncomingChatCapacity - over_capacity (no free slots):',
      capacityState,
    )
    return { status: 'over_capacity', slot: null }
  }

  /**
   * Whether the coach's configured preference accepts new chat requests.
   * Intentional "away"/inactive and post-chat cool-down always block new chats.
   * @returns {boolean}
   */
  _isCoachAcceptingNewChats() {
    if (this._isInPostChatCooldown()) {
      return false
    }
    const availability = this.connectionState.availability ?? 'away'
    return availability === 'text_only' || availability === 'audio_video'
  }

  _hasLiveAvailabilityPreference() {
    const availability = this.connectionState.availability ?? 'away'
    return availability === 'text_only' || availability === 'audio_video'
  }

  _isInPostChatCooldown() {
    return (
      this._postChatCooldownUntil != null &&
      Date.now() < this._postChatCooldownUntil
    )
  }

  _clearPostChatAvailabilityCooldown() {
    if (this._postChatAvailabilityTimer) {
      clearTimeout(this._postChatAvailabilityTimer)
      this._postChatAvailabilityTimer = null
    }
    this._postChatCooldownUntil = null
    if (this.connectionState) {
      this.connectionState.postChatCooldownActive = false
    }
  }

  /**
   * After the last active chat ends, stay unavailable for 30s so the coach can
   * switch to inactive / change mode before live presence is restored.
   */
  _startPostChatAvailabilityCooldown() {
    if (!this.rtcService) return

    this._clearPostChatAvailabilityCooldown()
    this._postChatCooldownUntil = Date.now() + POST_CHAT_AVAILABILITY_DELAY_MS
    this.connectionState.postChatCooldownActive = true

    console.log(
      '[CoachChatManager] Starting post-chat availability cool-down',
      {
        delayMs: POST_CHAT_AVAILABILITY_DELAY_MS,
        availability: this.connectionState.availability,
      },
    )

    this.rtcService.updateCoachPresence('away', 'Unavailable')
    this.rtcService.updateCurrentUserStatus('away')
    this.rtcService.updateCoachActiveTextChatCount(0)
    this.notifyListeners('stateChange', this.connectionState)

    this._postChatAvailabilityTimer = setTimeout(() => {
      this._postChatAvailabilityTimer = null
      this._postChatCooldownUntil = null
      if (this.connectionState) {
        this.connectionState.postChatCooldownActive = false
      }

      // Preference may have been set to away during the window — respect that
      console.log(
        '[CoachChatManager] Post-chat cool-down ended; syncing presence',
        { availability: this.connectionState?.availability },
      )
      this._updatePresenceForCapacity()
      this.notifyListeners('stateChange', this.connectionState)
    }, POST_CHAT_AVAILABILITY_DELAY_MS)
  }

  /**
   * After removing an active chat, either start the cool-down (last chat ended
   * while preference is live) or sync capacity-based presence immediately.
   * @param {number} previousActiveCount
   */
  _onActiveChatRemoved(previousActiveCount) {
    const nowActive = (this.connectionState.activeChats || []).length
    if (
      previousActiveCount > 0 &&
      nowActive === 0 &&
      this._hasLiveAvailabilityPreference()
    ) {
      this._startPostChatAvailabilityCooldown()
      return
    }
    this._updatePresenceForCapacity()
  }

  /**
   * Authoritatively reject an in-flight candidate request while inactive (CTS-9831).
   */
  _rejectIncomingCallDueToUnavailability({
    candidateEasyrtcid,
    conversationId,
    chatMode,
    candidateName,
    candidateId,
  }) {
    console.log(
      '[CoachChatManager] Rejecting incoming call — coach unavailable/away',
      {
        candidateEasyrtcid,
        conversationId,
        chatMode,
        availability: this.connectionState.availability,
      },
    )
    if (!candidateEasyrtcid) return

    this._sendChatResponse(candidateEasyrtcid, 'missed')
    this._recordMissedCallDueToCapacity({
      candidateEasyrtcid,
      conversationId,
      chatMode: chatMode || 'text',
      candidateName: candidateName || 'Unknown',
      candidateId: candidateId || candidateEasyrtcid,
    })
  }

  /**
   * Auto-miss any ringing requests when the coach goes inactive.
   */
  _declinePendingChatsDueToUnavailability() {
    const pendingChats = [...(this.connectionState.pendingChats || [])]
    if (pendingChats.length === 0) return

    pendingChats.forEach((pendingChat) => {
      const candidateEasyrtcid = pendingChat.candidateEasyrtcid
      if (!candidateEasyrtcid) return
      try {
        this._sendChatResponse(candidateEasyrtcid, 'missed')
      } catch (error) {
        console.error(
          'CoachChatManager - Error declining pending chat on away:',
          error,
        )
      }
      this._releasePendingChatSlot(pendingChat)
    })

    this.connectionState.pendingChats = []
    this._syncAvailableChatFromOccupancy()
    this._updatePresenceForCapacity()
    this.notifyListeners('stateChange', this.connectionState)
  }

  /**
   * Update coach presence/availability in the room based on current capacity.
   *
   * - If availability preference is away, always publish away (CTS-9831).
   * - If any audio/video chat is pending or active, or if there are 2 text chats,
   *   the coach is effectively busy (dnd) so candidates see them as busy.
   * - Otherwise, restore presence based on the configured availability
   *   ('text_only' or 'audio_video').
   *
   * This operates directly on presence/status and does NOT change the saved
   * availability preference.
   */
  _updatePresenceForCapacity() {
    try {
      if (!this.rtcService) return

      const activeChats = this.connectionState.activeChats || []
      const pendingChats = this.connectionState.pendingChats || []

      const hasAvChat =
        activeChats.some(
          (chat) => chat.chatMode === 'audio' || chat.chatMode === 'video',
        ) ||
        pendingChats.some(
          (chat) => chat.chatMode === 'audio' || chat.chatMode === 'video',
        )

      const activeTextChats = activeChats.filter(
        (chat) => chat.chatMode === 'text' || !chat.chatMode,
      )
      const pendingTextChats = pendingChats.filter(
        (chat) => chat.chatMode === 'text' || !chat.chatMode,
      )
      const totalTextChats = activeTextChats.length + pendingTextChats.length

      const availability = this.connectionState.availability ?? 'away'

      // Intentional inactive always wins — do not restore live presence from capacity
      if (availability === 'away') {
        console.log(
          '[CoachChatManager] _updatePresenceForCapacity - preference is away',
          { hasAvChat, totalTextChats },
        )
        this.rtcService.updateCoachPresence('away', 'Unavailable')
        this.rtcService.updateCurrentUserStatus('away')
        this.rtcService.updateCoachActiveTextChatCount(activeTextChats.length)
        return
      }

      // Post-chat cool-down: stay unavailable so coach can go inactive / change mode
      if (this._isInPostChatCooldown()) {
        console.log(
          '[CoachChatManager] _updatePresenceForCapacity - post-chat cool-down active',
          {
            availability,
            cooldownUntil: this._postChatCooldownUntil,
            hasAvChat,
            totalTextChats,
          },
        )
        this.rtcService.updateCoachPresence('away', 'Unavailable')
        this.rtcService.updateCurrentUserStatus('away')
        this.rtcService.updateCoachActiveTextChatCount(activeTextChats.length)
        return
      }

      // At capacity: publish dnd (busy) so candidates see busy, not "gone"
      if (hasAvChat || totalTextChats >= 2) {
        console.log(
          '[CoachChatManager] _updatePresenceForCapacity - at capacity, dnd',
          {
            availability,
            hasAvChat,
            totalTextChats,
            activeTextChatCount: activeTextChats.length,
          },
        )
        this.rtcService.updateCoachPresence('dnd', 'Busy')
        this.rtcService.updateCurrentUserStatus('dnd')
        this.rtcService.updateCoachActiveTextChatCount(activeTextChats.length)
        return
      }

      // 1 text chat + audio_video preference => downgrade room presence to text_only
      let effectiveAvailability = availability
      if (
        !hasAvChat &&
        totalTextChats === 1 &&
        availability === 'audio_video'
      ) {
        effectiveAvailability = 'text_only'
      }

      let presenceMessage
      if (this.rtcService.getAvailabilityPresenceMessage) {
        presenceMessage = this.rtcService.getAvailabilityPresenceMessage(
          effectiveAvailability,
        )
      } else if (effectiveAvailability === 'text_only') {
        presenceMessage = 'Available for text chat'
      } else if (effectiveAvailability === 'audio_video') {
        presenceMessage = 'Available for audio, video, & text chat'
      } else {
        presenceMessage = 'Unavailable'
      }

      console.log(
        '[CoachChatManager] _updatePresenceForCapacity - applying effective availability',
        {
          availability,
          effectiveAvailability,
          presenceMessage,
          hasAvChat,
          totalTextChats,
          activeTextChatCount: activeTextChats.length,
        },
      )
      this.rtcService.updateCoachPresence(
        effectiveAvailability,
        presenceMessage,
      )
      this.rtcService.updateCurrentUserStatus(effectiveAvailability)
      // CTS-9526: publish active text-chat load for candidate-side soft balancing
      this.rtcService.updateCoachActiveTextChatCount(activeTextChats.length)
    } catch (error) {
      console.error(
        'CoachChatManager - Error in _updatePresenceForCapacity:',
        error,
      )
    }
  }

  _setupManagerCommunication() {
    // RTC service events
    this.rtcService.addEventListener('connected', (state) => {
      /*
      console.log(
        'CoachChatManager - RTC connected event received, state:',
        state,
      )
      */
      /*
      console.log(
        'CoachChatManager - Previous connectionState:',
        this.connectionState,
      )
      */
      this.connectionState = { ...this.connectionState, ...state }
      /*
      console.log(
        'CoachChatManager - Updated connectionState after connected:',
        this.connectionState,
      )
      */

      // Update MediaService with EasyRTC instance now that connection is established
      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc) {
        this.mediaService.setEasyRTC(easyrtc)
      }

      // Sync availability preference after connect (presence may already be set in joinRoom)
      if (this.connectionState.availability) {
        this.setAvailability(this.connectionState.availability, { force: true })
      }

      this.notifyListeners('connected', this.connectionState)
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.rtcService.addEventListener('disconnected', (state) => {
      /*
      console.log(
        'CoachChatManager - RTC disconnected event received, state:',
        state,
      )
      */
      /*
      console.log(
        'CoachChatManager - Previous connectionState:',
        this.connectionState,
      )
      */
      this.connectionState = { ...this.connectionState, ...state }
      /*
      console.log(
        'CoachChatManager - Updated connectionState after disconnected:',
        this.connectionState,
      )
      */
      this.notifyListeners('disconnected', this.connectionState)
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.rtcService.addEventListener('candidatesUpdate', (candidates) => {
      /*
      console.log(
        'CoachChatManager - candidatesUpdate event received, candidates:',
        candidates,
      )
      */
      /*
      console.log(
        'CoachChatManager - Previous candidates:',
        this.connectionState.candidates,
      )
      */
      const activeChats = this.connectionState.activeChats || []
      const easyrtc = this.rtcService.getEasyRTC?.()
      const chatRoom = this.connectionState.chatRoom
      const roomOccupants =
        easyrtc && chatRoom ? easyrtc.getRoomOccupantsAsMap(chatRoom) : null
      const candidateEasyrtcids = (candidates || []).map((c) => c.easyrtcid)

      // Detect peers who left the room. Prefer the room occupants map — it is the
      // source of truth. A transient empty `candidates` array must NOT treat every
      // active chat as gone (causes "Candidate has left" on the other slot).
      if (activeChats.length > 0) {
        if (roomOccupants) {
          for (const activeChat of activeChats) {
            const id = activeChat.candidateEasyrtcid
            if (!id) continue
            if (roomOccupants[id] == null) {
              this.handleCandidateStreamClosed({ easyrtcid: id })
            }
          }
        } else if (candidateEasyrtcids.length > 0) {
          for (const activeChat of activeChats) {
            const id = activeChat.candidateEasyrtcid
            if (!id) continue
            if (!candidateEasyrtcids.includes(id)) {
              this.handleCandidateStreamClosed({ easyrtcid: id })
            }
          }
        }
      }
      this.connectionState.candidates = candidates
      /*
      console.log(
        'CoachChatManager - Updated candidates in connectionState:',
        this.connectionState.candidates,
      )
      */
      this.notifyListeners('candidatesUpdate', candidates)
    })

    this.rtcService.addEventListener('dataChannelOpen', (data) => {
      this.notifyListeners('dataChannelOpen', data)
    })

    this.rtcService.addEventListener('peerMessage', (data) => {
      // console.log('CoachChatManager - Received peer message:', data)

      // Handle candidate pause/mute events
      if (
        data.messageType === 'video_paused' ||
        data.messageType === 'video_unpaused' ||
        data.messageType === 'audio_muted' ||
        data.messageType === 'audio_unmuted'
      ) {
        this.handleCandidateMediaStateChange(data)
      }

      // Handle text_disabled / text_enabled (candidate has text chat hidden, e.g. mobile video)
      const actionType =
        data.message?.actionType || data.messageData?.action_type
      if (
        data.messageType === 'candidate_action' &&
        (actionType === 'text_disabled' || actionType === 'text_enabled')
      ) {
        const senderEasyrtcid =
          data.fromEasyrtcid || data.message?.fromEasyrtcid
        if (senderEasyrtcid && this.connectionState.activeChats?.length) {
          const textDisabled = actionType === 'text_disabled'
          const nextActiveChats = this.connectionState.activeChats.map(
            (chat) =>
              chat.candidateEasyrtcid === senderEasyrtcid
                ? { ...chat, textDisabled }
                : chat,
          )
          this.connectionState = {
            ...this.connectionState,
            activeChats: nextActiveChats,
          }
          this.notifyListeners('stateChange', this.connectionState)
        }
        this.notifyListeners('peerMessage', data)
        return
      }

      // If the message includes a formatted message object, add it (skip when null, e.g. text_disabled)
      if (data.message != null) {
        // Find the active chat for this sender to associate message with chat slot
        const senderEasyrtcid = data.fromEasyrtcid || data.message.senderId
        if (senderEasyrtcid) {
          const activeChat = this.connectionState.activeChats?.find(
            (chat) => chat.candidateEasyrtcid === senderEasyrtcid,
          )

          if (activeChat) {
            // Add chat slot info to message
            data.message.chatSlot = activeChat.chatSlot
            data.message.chatKey = activeChat.chatKey
            data.message.chatId = activeChat.id
          }
        }

        this.addMessage(data.message)
      }
      this.notifyListeners('peerMessage', data)
    })

    // Handle when candidate ends the chat
    this.rtcService.addEventListener('chatEndedByCandidate', (data) => {
      this.handleChatEndedByCandidate(data)
    })

    // Handle typing indicators from candidate
    this.rtcService.addEventListener('candidateTyping', (data) => {
      this.notifyListeners('candidateTyping', data)
    })

    // Handle incoming chat requests from candidates
    this.rtcService.addEventListener('incomingChatRequest', (data) => {
      this.handleIncomingChatRequest(data)
    })

    // Handle incoming calls from candidates (this is the main event for call acceptance)
    this.rtcService.addEventListener('incomingCall', (data) => {
      // console.log('CoachChatManager - Received incomingCall event:', data)
      this.handleIncomingCall(data)
    })

    // Handle call_missed messages from candidates (when candidate times out)
    this.rtcService.addEventListener('callMissed', (data) => {
      // console.log('CoachChatManager - Received callMissed event:', data)
      this.handleCallMissed(data)
    })

    // Handle call_cancelled messages from candidates (when candidate closes before coach answers)
    this.rtcService.addEventListener('callCancelled', (data) => {
      this.handleCallCancelled(data)
    })

    // Handle EasyRTC peer connection calls (these come after call_accepted)
    this.rtcService.addEventListener('easyrtcCall', (data) => {
      // console.log('CoachChatManager - Received easyrtcCall event:', data)
      this.handleEasyrtcCall(data)
    })

    // Handle coaches list updates
    this.rtcService.addEventListener('coachesUpdate', (coaches) => {
      this.connectionState.coaches = coaches
      this.notifyListeners('coachesUpdate', coaches)
      this.notifyListeners('stateChange', this.connectionState)
    })

    // Handle candidate info updates
    this.rtcService.addEventListener('candidateInfoUpdated', (data) => {
      this.notifyListeners('candidateInfoUpdated', data)
    })

    // Handle candidates update to fetch candidate info (similar to legacy chat.js lines 2406-2423)
    this.rtcService.addEventListener('candidatesUpdate', (candidates) => {
      this._seedCandidateDisplayCacheFromRoomCandidates(candidates)
      this.connectionState.candidates =
        this.applyCachedDisplayNamesToCandidates(candidates)
      this._fetchCandidateInfoForNewCandidates(candidates)
      this.notifyListeners('candidatesUpdate', this.connectionState.candidates)
      this.notifyListeners('stateChange', this.connectionState)
    })

    // Handle coach list updates
    this.rtcService.addEventListener('coachListUpdated', (data) => {
      this.notifyListeners('coachListUpdated', data)
    })

    // Handle presence updates
    this.rtcService.addEventListener('presenceUpdated', (data) => {
      this.notifyListeners('presenceUpdated', data)
    })

    // Handle when coach ends the chat
    this.rtcService.addEventListener('chatEndedByCoach', (data) => {
      this.handleChatEndedByCoach(data)
    })

    // Handle chat acceptance/rejection responses from candidates
    this.rtcService.addEventListener('chatResponse', (data) => {
      this.handleChatResponse(data)
    })

    // Handle remote stream received from candidate
    this.rtcService.addEventListener('remoteStreamReceived', (data) => {
      // console.log('CoachChatManager - Remote stream received:', data)
      this.connectionState.remoteStream = data.stream
      this.connectionState.hasRemoteVideo = data.hasVideo
      this.connectionState.hasRemoteAudio = data.hasAudio

      // Set up remote video element using ref
      if (this.videoRefs.callerVideo && data.stream) {
        this.videoRefs.callerVideo.srcObject = data.stream
        this.videoRefs.callerVideo.play().catch((error) => {
          console.warn('Could not autoplay remote video:', error)
        })
      }

      this.notifyListeners('remoteVideoReady', data)
      this.notifyListeners('stateChange', this.connectionState)
    })

    // When candidate's peer stream closes (e.g. mobile lock, disconnect), show "candidate has left"
    this.rtcService.addEventListener('streamClosed', (data) => {
      this.handleCandidateStreamClosed(data)
    })

    // Handle local video ready events (from our own media initialization)
    this.addEventListener('localVideoReady', (data) => {
      // console.log('CoachChatManager - Local video ready event received:', data)
      this.connectionState.hasLocalVideo = data.hasLocalVideo
      this.connectionState.hasLocalAudio = data.hasLocalAudio
      this.connectionState.localStream = data.stream
      // CTS-9777: MediaService must own the stream for pause/mute track toggles
      if (data.stream && this.mediaService) {
        this.mediaService.setLocalStream(data.stream)
      }
      this.notifyListeners('stateChange', this.connectionState)
    })

    this.addEventListener('localAudioReady', (data) => {
      this.connectionState.hasLocalVideo = data.hasLocalVideo
      this.connectionState.hasLocalAudio = data.hasLocalAudio
      this.connectionState.localStream = data.stream
      if (data.stream && this.mediaService) {
        this.mediaService.setLocalStream(data.stream)
      }
      this.notifyListeners('stateChange', this.connectionState)
    })
  }

  _initializeMediaService() {
    // Initialize media service with coach-specific settings
    this.mediaService.initialize({
      enableAudio: false,
      enableVideo: false,
      enableDataChannels: false,
      autoInitUserMedia: false,
    })
    this._setupMediaServiceEvents()
  }

  /**
   * Wire MediaService events so mute/pause UI stays in sync and peers are notified.
   * CTS-9777 / CC-UX-012 — coach previously called toggleAudioMute without listening
   * for audioMuteChanged / sendDataChannelMessage (candidate side already does this).
   */
  _setupMediaServiceEvents() {
    this.mediaService.addEventListener('audioMuteChanged', (data) => {
      this.connectionState.audioMuted = data.muted
      this.notifyListeners('audioMuteChanged', data)
      this.notifyListeners('stateChange', this.getConnectionState())
    })

    this.mediaService.addEventListener('videoPauseChanged', (data) => {
      this.connectionState.videoPaused = data.paused
      this.notifyListeners('videoPauseChanged', data)
      this.notifyListeners('stateChange', this.getConnectionState())
    })

    this.mediaService.addEventListener('sendDataChannelMessage', (data) => {
      this.sendDataChannelMessage(data.messageType, data.messageData)
    })
  }

  /**
   * Notify the active audio/video candidate of a media control change
   * (e.g. audio_muted / audio_unmuted / video_paused / video_unpaused).
   */
  sendDataChannelMessage(messageType, messageData) {
    try {
      const activeChat = (this.connectionState.activeChats || []).find(
        (chat) => chat.chatMode === 'audio' || chat.chatMode === 'video',
      )
      const candidateEasyrtcid =
        activeChat?.candidateEasyrtcid ||
        this.connectionState.selectedCandidate?.easyrtcid

      if (!candidateEasyrtcid) {
        return false
      }

      const easyrtc = this.rtcService.getEasyRTC()
      if (!easyrtc) {
        return false
      }

      easyrtc.sendData(candidateEasyrtcid, messageType, messageData)
      return true
    } catch (error) {
      console.error(
        'CoachChatManager - Error sending data channel message:',
        error,
      )
      return false
    }
  }

  // Main connection method
  async connect(socketUrl, username, chatRoom, options = {}) {
    // Store coach username for chat history (like legacy self.username)
    this.coachUsername = username

    // Set logging properties from options (only if provided)
    if (options.logChatEventPath !== undefined) {
      this.logChatEventPath = options.logChatEventPath
    }
    if (options.logChatMissedOpportunityPath !== undefined) {
      this.logChatMissedOpportunityPath = options.logChatMissedOpportunityPath
    }
    if (options.buildChatHistoryPath !== undefined) {
      this.buildChatHistoryPath = options.buildChatHistoryPath
    }
    if (options.timeOffset !== undefined) {
      this.serverTimeOffset = options.timeOffset
    }

    // Pass logging configuration to RtcService
    if (this.logChatEventPath) {
      this.rtcService.setLoggingConfig(
        this.logChatEventPath,
        this.serverTimeOffset,
      )
    }

    // Store paths from options
    if (options.coachChatCandidateInfoPath !== undefined) {
      this.coachChatCandidateInfoPath = options.coachChatCandidateInfoPath
    }
    if (options.coachEndChatPath !== undefined) {
      this.coachEndChatPath = options.coachEndChatPath
      // console.log(
      //   '[CoachChatManager] connect - coachEndChatPath set to:',
      //   this.coachEndChatPath,
      // )
    }

    // Set coach-specific options
    const coachOptions = {
      ...options,
      isCoach: true,
      appName: 'yns.coach_chat',
      presenceMessage: 'Coach Chat',
    }

    // Persist preference before join so incoming-call gates see the truth (CTS-9831)
    if (options.availability !== undefined) {
      this.connectionState.availability = options.availability
      this.connectionState.isAvailable = options.availability !== 'away'
    } else if (!this.connectionState.availability) {
      this.connectionState.availability = 'away'
      this.connectionState.isAvailable = false
    }

    return this.rtcService.connect(socketUrl, username, chatRoom, coachOptions)
  }

  // Event logging function (matches old chat.js pattern)
  logEvent(eventType, info, conversationId) {
    if (!this.logChatEventPath) {
      console.warn(
        'CoachChatManager - logChatEventPath not set, cannot log event:',
        eventType,
      )
      return
    }

    const ts = new Date().getTime()
    const nts = ts + this.serverTimeOffset
    const params = {
      event_type: eventType,
      occurred_at: ts,
      event_info: info,
      chat_token: this.connectionState.myEasyrtcid || 'unknown',
      conversation_id: conversationId,
      normalized_occurred_at: nts,
    }

    // Use fetch instead of jQuery for modern approach
    // Add AbortController to handle navigation gracefully
    const controller = new AbortController()

    // Set a timeout to abort the request if it takes too long
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, 5000) // 5 second timeout

    fetch(this.logChatEventPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        chat_event: params,
      }),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        return response.json()
      })
      .then((resp) => {
        if (resp.success === false) {
          console.warn('CoachChatManager - Event logging failed:', resp)
          if (
            resp.chat_event &&
            resp.chat_event.event_type === 'user_logged_out'
          ) {
            console.warn(
              'CoachChatManager - User logged out, connection may be invalid',
            )
          }
        }
        return resp
      })
      .catch((error) => {
        clearTimeout(timeoutId)

        // Don't log fetch errors if they're due to navigation or abort
        if (error.name === 'AbortError') {
          // Event logging aborted (likely due to navigation)
        } else if (
          error.name === 'TypeError' &&
          error.message.includes('Failed to fetch')
        ) {
          // Event logging failed due to navigation or network issue
        } else {
          console.error('CoachChatManager - Error logging event:', error)
        }
      })
  }

  // Log a missed opportunity record using the same endpoint as legacy chat.js.
  // missedType: 'missed' | 'rejected' | 'busy'
  // requestType: chat mode ('text', 'audio', 'video') — null for 'busy'
  // candidateToken: candidate's chat token (other_chat_token) — null for 'busy'
  logMissedOpportunity(missedType, requestType = null, candidateToken = null) {
    if (!this.logChatMissedOpportunityPath) {
      console.warn(
        'CoachChatManager - logChatMissedOpportunityPath not set, cannot log missed opportunity:',
        missedType,
      )
      return
    }

    const ts = new Date().getTime()
    const params = {
      occurred_at: ts,
      missed_type: missedType,
      request_type: requestType,
      chat_token: this.coachUsername || '',
      other_chat_token: candidateToken,
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    fetch(this.logChatMissedOpportunityPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({ chat_missed_opportunity: params }),
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId)
        return response.json()
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (error.name !== 'AbortError') {
          console.error(
            'CoachChatManager - Error logging missed opportunity:',
            error,
          )
        }
      })
  }

  // Find available chat slot (1 or 2, max 2 concurrent chats)
  _findAvailableChatSlot() {
    const chat1 = this.connectionState.chats['chat-1']
    const chat2 = this.connectionState.chats['chat-2']

    if (!chat1.candidateId || chat1.candidateId === '') {
      return 1
    }
    if (!chat2.candidateId || chat2.candidateId === '') {
      return 2
    }
    return null // No available slot
  }

  /**
   * Prefer live occupancy (active + pending) over stale chats[] metadata when
   * deciding which slot is free. chats[] can lag after reject/end edge cases.
   */
  _syncAvailableChatFromOccupancy() {
    const activeAndPending = [
      ...(this.connectionState.activeChats || []),
      ...(this.connectionState.pendingChats || []),
    ]
    const slot1InUse = activeAndPending.some(
      (chat) => (chat.chatSlot || 1) === 1,
    )
    const slot2InUse = activeAndPending.some((chat) => chat.chatSlot === 2)

    if (!slot1InUse) {
      this.connectionState.availableChat = 1
      return 1
    }
    if (!slot2InUse) {
      this.connectionState.availableChat = 2
      return 2
    }
    this.connectionState.availableChat = null
    return null
  }

  _findPendingChatByCandidateId(candidateId) {
    if (!candidateId) return null
    return (
      this.connectionState.pendingChats?.find(
        (chat) =>
          chat.candidateId === candidateId ||
          chat.candidateEasyrtcid === candidateId ||
          chat.easyrtcid === candidateId,
      ) || null
    )
  }

  _releasePendingChatSlot(pendingChat) {
    const chatKey =
      pendingChat?.chatKey ||
      (pendingChat?.chatSlot ? `chat-${pendingChat.chatSlot}` : null)

    if (!chatKey) {
      return
    }

    const slotIsStillInUse = [
      ...(this.connectionState.activeChats || []),
      ...(this.connectionState.pendingChats || []).filter(
        (chat) => chat !== pendingChat,
      ),
    ].some((chat) => (chat.chatKey || `chat-${chat.chatSlot || 1}`) === chatKey)

    if (slotIsStillInUse) {
      return
    }

    const chatSlotData = this.connectionState.chats[chatKey]
    if (!chatSlotData) {
      return
    }

    chatSlotData.candidateId = ''
    chatSlotData.chatMode = ''
    chatSlotData.conversationId = ''
    chatSlotData.chatHistory = {
      messages: [],
      errors: [],
      start_time: null,
      end_time: null,
      chat_type: null,
      coach_token: null,
      candidate_token: null,
    }
  }

  /**
   * When the coach is at capacity, no pending chat is created so handleCallMissed never runs.
   * Submits the same chat history / missed-opportunity logging as a normal missed call so the
   * coach sees it in recent chat history.
   */
  _recordMissedCallDueToCapacity({
    candidateEasyrtcid,
    conversationId,
    chatMode,
    candidateName,
    candidateId,
  }) {
    try {
      const easyrtc = this.rtcService.getEasyRTC()
      const candidateToken =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'username',
        ) ||
        easyrtc?.idToName(candidateEasyrtcid) ||
        candidateId ||
        candidateEasyrtcid

      const now = Date.now()
      const mode = chatMode || 'text'

      const chatInfo = {
        messages: [
          {
            who: this.coachUsername || 'Coach',
            text: 'Chat request missed by coach',
            ts: now,
          },
        ],
        start_time: now,
        end_time: now,
        chat_type: mode,
        coach_token: this.coachUsername || '',
        candidate_token: candidateToken,
        conversation_id: conversationId || '',
      }

      if (this.coachEndChatPath) {
        this._submitChatHistory(chatInfo)
      }

      this.logEvent(
        'missed_opportunity',
        {
          missed_type: 'missed',
          request_type: mode,
          candidate_token: candidateToken,
        },
        conversationId,
      )
      this.logMissedOpportunity('missed', mode, candidateToken)

      const missedChatEntry = {
        id: now,
        candidateId,
        candidateName: candidateName || 'Unknown',
        candidateEasyrtcid,
        conversationId,
        chatMode: mode,
        status: 'missed',
        endedAt: new Date(),
        endedAtTimestamp: now,
        timestamp: new Date(),
      }

      this.connectionState.chatHistory = [
        ...(this.connectionState.chatHistory || []),
        missedChatEntry,
      ]

      this.notifyListeners('callMissed', {
        candidateEasyrtcid,
        candidateName: candidateName || 'Unknown',
      })
      this.notifyListeners('chatEnded', missedChatEntry)
      this.notifyListeners('stateChange', this.connectionState)
    } catch (error) {
      console.error(
        'CoachChatManager - Error recording capacity missed call:',
        error,
      )
    }
  }

  // Coach-specific methods
  acceptChat(candidateId) {
    try {
      console.log(
        '[CoachChatManager] acceptChat - called with candidateId:',
        candidateId,
      )

      // Check if we already have 2 active chats (max limit)
      if (this.connectionState.activeChats.length >= 2) {
        console.warn(
          'CoachChatManager - Maximum concurrent chats (2) reached, cannot accept more',
        )
        return false
      }

      // Find the pending chat for this candidate (id or easyrtcid)
      const pendingChat = this._findPendingChatByCandidateId(candidateId)

      if (!pendingChat) {
        console.warn(
          '[CoachChatManager] acceptChat - No pendingChat found for candidateId:',
          candidateId,
        )
        return false
      }

      // Use the slot that was assigned when the chat became pending, falling back to finder only if missing
      const chatSlot =
        pendingChat.chatSlot || this._syncAvailableChatFromOccupancy()
      if (!chatSlot) {
        console.warn(
          '[CoachChatManager] acceptChat - No available chat slot, cannot accept chat',
        )
        return false
      }

      const chatKey = pendingChat.chatKey || `chat-${chatSlot}`
      const chatSlotData = this.connectionState.chats[chatKey]

      console.log('[CoachChatManager] acceptChat - using slot/key:', {
        chatSlot,
        chatKey,
        pendingChat,
      })

      // Get coach token and candidate token (matching legacy chat.js lines 421-422)
      const easyrtc = this.rtcService.getEasyRTC()
      // Coach token: use stored username (like legacy self.username)
      const coachToken = this.coachUsername || ''
      // Candidate token: use easyrtc.idToName() as primary, fallback to room API field or candidateId
      const candidateToken =
        (easyrtc && easyrtc.idToName(candidateId)) ||
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateId,
          'chatToken',
        ) ||
        candidateId ||
        ''

      // Initialize chat slot with history tracking (similar to legacy chat.js lines 419-422)
      chatSlotData.candidateId = candidateId
      chatSlotData.chatMode = pendingChat.chatMode || 'text'
      chatSlotData.conversationId = pendingChat.conversationId || ''
      chatSlotData.chatHistory.start_time = Date.now()
      chatSlotData.chatHistory.chat_type = pendingChat.chatMode || 'text'
      chatSlotData.chatHistory.coach_token = coachToken
      chatSlotData.chatHistory.candidate_token = candidateToken

      // Move from pending to active
      this.connectionState.pendingChats =
        this.connectionState.pendingChats.filter(
          (chat) =>
            chat !== pendingChat &&
            chat.id !== pendingChat.id &&
            chat.candidateEasyrtcid !== pendingChat.candidateEasyrtcid,
        )

      const activeChat = {
        ...pendingChat,
        status: 'active',
        chatSlot: chatSlot,
        chatKey: chatKey,
        startTime: Date.now(),
        candidateToken: candidateToken, // Store candidateToken for caching when candidate leaves
      }

      this.connectionState.activeChats = [
        ...(this.connectionState.activeChats || []),
        activeChat,
      ]

      // Remaining free slot from live occupancy (not "the other slot" blindly)
      this._syncAvailableChatFromOccupancy()

      // Update chat status
      this.connectionState.chatStatus = 'chat_active'

      // Set selectedCandidate or selectedCandidate2 based on chat slot
      const selectedCandidateData = {
        ...pendingChat,
        easyrtcid: pendingChat.candidateEasyrtcid,
        status: 'active',
        chatSlot: chatSlot,
        chatKey: chatKey,
      }

      if (chatSlot === 1) {
        this.connectionState.selectedCandidate = selectedCandidateData
      } else {
        this.connectionState.selectedCandidate2 = selectedCandidateData
      }

      // Update presence based on new capacity (may keep coach busy if A/V or 2 texts)
      this._updatePresenceForCapacity()

      // Notify listeners
      this.notifyListeners('chatStarted', activeChat)
      this.notifyListeners('stateChange', this.connectionState)

      // Send acceptance message to candidate
      this._sendChatResponse(pendingChat.candidateEasyrtcid, 'accepted')

      // Fetch candidate info for this chat
      this.fetchCandidateInfo(candidateToken)

      return true
    } catch (error) {
      console.error('CoachChatManager - Error accepting chat:', error)
      return false
    }
  }

  declineChat(candidateId) {
    try {
      // Find the pending chat for this candidate (id or easyrtcid)
      const pendingChat = this._findPendingChatByCandidateId(candidateId)

      if (!pendingChat) {
        return false
      }

      const candidateEasyrtcid = pendingChat.candidateEasyrtcid

      // Get candidate info from room
      const easyrtc = this.rtcService.getEasyRTC()
      const chatMode =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'chatMode',
        ) ||
        pendingChat.chatMode ||
        'text'
      const conversationId =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'conversationId',
        ) || pendingChat.conversationId
      // Get candidate token (username)
      const candidateToken =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'username',
        ) ||
        easyrtc?.idToName(candidateEasyrtcid) ||
        pendingChat.candidateId ||
        candidateEasyrtcid

      // Rejected chats were never accepted, so they must not create a persisted
      // chat transcript or borrow another conversation's slot history.
      this.logEvent(
        'missed_opportunity',
        {
          missed_type: 'rejected',
          request_type: chatMode,
          candidate_token: candidateToken,
        },
        conversationId,
      )
      this.logMissedOpportunity('rejected', chatMode, candidateToken)

      this._releasePendingChatSlot(pendingChat)

      const rejectedChatEntry = {
        id: pendingChat.id,
        candidateId: pendingChat.candidateId,
        candidateName: pendingChat.candidateName,
        candidateEasyrtcid: candidateEasyrtcid,
        conversationId: conversationId,
        chatMode: chatMode,
        status: 'rejected',
        endedAt: new Date(),
        endedAtTimestamp: Date.now(),
        timestamp: pendingChat.timestamp,
      }
      this.connectionState.chatHistory = [
        ...(this.connectionState.chatHistory || []),
        rejectedChatEntry,
      ]

      // Send call_missed message to candidate (so they treat it as missed, not rejected)
      // This ensures candidate sees it as a missed call, not a rejection
      this._sendChatResponse(pendingChat.candidateEasyrtcid, 'missed')

      // Remove from pending chats
      this.connectionState.pendingChats =
        this.connectionState.pendingChats.filter(
          (chat) =>
            chat !== pendingChat &&
            chat.id !== pendingChat.id &&
            chat.candidateEasyrtcid !== candidateEasyrtcid,
        )

      this._syncAvailableChatFromOccupancy()
      this._updatePresenceForCapacity()

      // Notify listeners to stop ringing
      this.notifyListeners('callMissed', {
        candidateEasyrtcid,
        candidateName: pendingChat.candidateName,
      })

      // Also fire chatEnded event so RecentChats component can refresh (similar to handleCallMissed)
      const rejectedChatForEvent = {
        id: pendingChat.id,
        candidateId: pendingChat.candidateId,
        candidateName: pendingChat.candidateName,
        candidateEasyrtcid: candidateEasyrtcid,
        conversationId: conversationId,
        chatMode: chatMode,
        status: 'rejected',
        endedAt: new Date(),
        endedAtTimestamp: Date.now(),
        timestamp: pendingChat.timestamp,
      }
      this.notifyListeners('chatEnded', rejectedChatForEvent)
      this.notifyListeners('stateChange', this.connectionState)

      return true
    } catch (error) {
      console.error('CoachChatManager - Error declining chat:', error)
      return false
    }
  }

  handleCallCancelled(data) {
    try {
      const candidateEasyrtcid = data.fromEasyrtcid

      // Find the pending chat for this candidate
      const pendingChat = this.connectionState.pendingChats?.find(
        (chat) => chat.candidateEasyrtcid === candidateEasyrtcid,
      )

      if (!pendingChat) {
        return
      }

      this._releasePendingChatSlot(pendingChat)

      // Remove from pending chats and stop ringing
      this.connectionState.pendingChats =
        this.connectionState.pendingChats.filter(
          (chat) => chat.candidateEasyrtcid !== candidateEasyrtcid,
        )

      this._syncAvailableChatFromOccupancy()
      this._updatePresenceForCapacity()

      // Notify listeners to stop ringing
      this.notifyListeners('callCancelled', {
        candidateEasyrtcid,
        candidateName: pendingChat.candidateName,
      })

      // Also notify state change
      this.notifyListeners('stateChange', this.connectionState)
    } catch (error) {
      console.error('CoachChatManager - Error handling call cancelled:', error)
    }
  }

  handleCallMissed(data) {
    try {
      const candidateEasyrtcid =
        typeof data === 'string' ? data : data?.fromEasyrtcid

      // Find the pending chat for this candidate
      const pendingChat = this.connectionState.pendingChats?.find(
        (chat) => chat.candidateEasyrtcid === candidateEasyrtcid,
      )

      if (!pendingChat) {
        // console.log(
        //   'CoachChatManager - handleCallMissed: No pending chat found for:',
        //   candidateEasyrtcid,
        // )
        return
      }

      // Get candidate info from room
      const easyrtc = this.rtcService.getEasyRTC()
      const chatMode =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'chatMode',
        ) ||
        pendingChat.chatMode ||
        'text'
      const conversationId =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'conversationId',
        ) || pendingChat.conversationId
      // Get candidate token (username) - try room API field first, then idToName, then fallback
      const candidateToken =
        easyrtc?.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'username',
        ) ||
        easyrtc?.idToName(candidateEasyrtcid) ||
        pendingChat.candidateId ||
        candidateEasyrtcid

      // Missed chats were never accepted, so they must not create a persisted
      // chat transcript or borrow another conversation's slot history.
      this.logEvent(
        'missed_opportunity',
        {
          missed_type: 'missed',
          request_type: chatMode,
          candidate_token: candidateToken,
        },
        conversationId,
      )
      this.logMissedOpportunity('missed', chatMode, candidateToken)

      this._releasePendingChatSlot(pendingChat)

      const missedChatEntry = {
        id: pendingChat.id,
        candidateId: pendingChat.candidateId,
        candidateName: pendingChat.candidateName,
        candidateEasyrtcid: candidateEasyrtcid,
        conversationId: conversationId,
        chatMode: chatMode,
        status: 'missed',
        endedAt: new Date(),
        endedAtTimestamp: Date.now(),
        timestamp: pendingChat.timestamp,
      }
      this.connectionState.chatHistory = [
        ...(this.connectionState.chatHistory || []),
        missedChatEntry,
      ]

      // Send chat_ended message to candidate
      this._sendChatEnded(candidateEasyrtcid)

      // Remove from pending chats
      this.connectionState.pendingChats =
        this.connectionState.pendingChats.filter(
          (chat) => chat.candidateEasyrtcid !== candidateEasyrtcid,
        )

      this._syncAvailableChatFromOccupancy()

      // Notify listeners to stop ringing
      this.notifyListeners('callMissed', {
        candidateEasyrtcid,
        candidateName: pendingChat.candidateName,
      })

      // Also fire chatEnded event so RecentChats component can refresh (similar to endChat)
      // Create a chat entry for the event (matching the structure expected by RecentChats)
      const missedChatForEvent = {
        id: pendingChat.id,
        candidateId: pendingChat.candidateId,
        candidateName: pendingChat.candidateName,
        candidateEasyrtcid: candidateEasyrtcid,
        conversationId: conversationId,
        chatMode: chatMode,
        status: 'missed',
        endedAt: new Date(),
        endedAtTimestamp: Date.now(),
        timestamp: pendingChat.timestamp,
      }
      this.notifyListeners('chatEnded', missedChatForEvent)
      this.notifyListeners('stateChange', this.connectionState)
      // Update presence since capacity has changed after a missed call
      this._updatePresenceForCapacity()
    } catch (error) {
      console.error('CoachChatManager - Error handling call missed:', error)
    }
  }

  endChat(chatId = null) {
    try {
      if (!chatId && this.connectionState.selectedCandidate) {
        chatId = this.connectionState.selectedCandidate.id
      }

      if (chatId) {
        // Find the active chat
        const activeChat = this.connectionState.activeChats?.find(
          (chat) => chat.id === chatId || chat.candidateEasyrtcid === chatId,
        )

        if (activeChat) {
          const previousActiveCount = (this.connectionState.activeChats || [])
            .length

          // Get chat slot data
          const chatKey =
            activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`
          const chatSlotData = this.connectionState.chats[chatKey]

          if (chatSlotData) {
            // Update chat history with end time
            chatSlotData.chatHistory.end_time = Date.now()

            // Build chat history JSON for backend submission (similar to legacy chat.js lines 1427-1434)
            // CTS-9775: fall back to activeChat.chatMode so video/audio rows keep the correct type
            const chatInfo = {
              messages: chatSlotData.chatHistory.messages || [],
              start_time: chatSlotData.chatHistory.start_time,
              end_time: chatSlotData.chatHistory.end_time,
              chat_type:
                chatSlotData.chatHistory.chat_type ||
                activeChat.chatMode ||
                chatSlotData.chatMode ||
                'text',
              coach_token: chatSlotData.chatHistory.coach_token || '',
              candidate_token: chatSlotData.chatHistory.candidate_token || '',
              conversation_id: chatSlotData.conversationId || '',
            }

            // Submit chat history to backend
            this._submitChatHistory(chatInfo)

            // Clear chat slot
            chatSlotData.candidateId = ''
            chatSlotData.chatMode = ''
            chatSlotData.conversationId = ''
            chatSlotData.chatHistory = {
              messages: [],
              errors: [],
              start_time: null,
              end_time: null,
              chat_type: null,
              coach_token: null,
              candidate_token: null,
            }
          }

          // Send coach_action "ended_chat" so it appears in candidate's message list, then notify chat ended
          this._sendCoachEndedChatAction(activeChat.candidateEasyrtcid)
          this._sendChatEnded(activeChat.candidateEasyrtcid)

          // Move from active to history
          this.connectionState.activeChats =
            this.connectionState.activeChats.filter(
              (chat) =>
                chat.id !== activeChat.id &&
                chat.candidateEasyrtcid !== activeChat.candidateEasyrtcid,
            )
          this.connectionState.chatHistory = [
            ...(this.connectionState.chatHistory || []),
            {
              ...activeChat,
              status: 'ended',
              endedAt: new Date(),
              endedAtTimestamp: Date.now(), // Store timestamp for easier comparison
            },
          ]

          // Clear selected candidate if it was this one (check both slots)
          const chatSlot = activeChat.chatSlot || 1
          if (chatSlot === 1) {
            if (
              this.connectionState.selectedCandidate?.id === activeChat.id ||
              this.connectionState.selectedCandidate?.candidateEasyrtcid ===
                activeChat.candidateEasyrtcid
            ) {
              this.connectionState.selectedCandidate = null
            }
          } else {
            if (
              this.connectionState.selectedCandidate2?.id === activeChat.id ||
              this.connectionState.selectedCandidate2?.candidateEasyrtcid ===
                activeChat.candidateEasyrtcid
            ) {
              this.connectionState.selectedCandidate2 = null
            }
          }

          // Remove only this chat's messages so other slots keep their thread
          this._removeMessagesForEndedChat(activeChat)

          // Free this slot; prefer slot 1 if both somehow free
          this._syncAvailableChatFromOccupancy()

          this._releaseChatMediaIfNeeded(activeChat)

          // Don't set chatStatus here - let getConnectionState compute it
          // This will automatically set it to 'waiting_for_candidate' if activeChats is now empty

          // Last active chat → 30s cool-down before restoring live presence
          this._onActiveChatRemoved(previousActiveCount)

          // Notify listeners
          this.notifyListeners('chatEnded', activeChat)
          this.notifyListeners('stateChange', this.connectionState)
        }
      }
    } catch (error) {
      console.error('CoachChatManager - Error ending chat:', error)
    }
  }

  sendMessage(content, chatId = null, selectedCandidate = null) {
    // console.log('CoachChatManager - sendMessage called, this:', this)
    /*
    console.log(
      'CoachChatManager - sendMessage connectionState:',
      this.connectionState,
    )
    */
    /*
    console.log(
      'CoachChatManager - sendMessage selectedCandidate:',
      this.connectionState?.selectedCandidate,
    )
    */

    try {
      // Safety check for connectionState
      if (!this.connectionState) {
        console.error(
          'CoachChatManager - connectionState is undefined, reinitializing...',
        )
        // Reinitialize connectionState if it's somehow undefined
        this.connectionState = {
          isConnected: false,
          isCoach: true,
          isAvailable: false,
          availability: 'away',
          videoEnabled: false,
          candidates: [],
          selectedCandidate: null,
          selectedCandidate2: null,
          activeChats: [],
          pendingChats: [],
          chatHistory: [],
          chatRoom: null,
          myEasyrtcid: null,
          messages: [],
          chatMode: 'text',
          chatStatus: 'disconnected',
          hasLocalVideo: false,
          hasLocalAudio: false,
          hasRemoteVideo: false,
          hasRemoteAudio: false,
          localStream: null,
          remoteStream: null,
        }
      }

      /*
      console.log('CoachChatManager - sendMessage called with:', {
        content,
        chatId,
      })
      */
      /*
      console.log(
        'CoachChatManager - selectedCandidate:',
        this.connectionState.selectedCandidate,
      )
      */
      // console.log('CoachChatManager - rtcService:', this.rtcService)
      // console.log('CoachChatManager - this:', this)

      // Check if rtcService is available
      if (!this.rtcService) {
        console.error('CoachChatManager - rtcService is not available')
        throw new Error('RTC service not initialized')
      }

      // Use provided selectedCandidate, or fall back to connectionState
      // Also check activeChats in case selectedCandidate is stale after reconnection
      let effectiveSelectedCandidate =
        selectedCandidate ||
        this.connectionState.selectedCandidate ||
        this.connectionState.selectedCandidate2

      // For coaches, we might not have a chatId but could have selectedCandidate
      if (!chatId && effectiveSelectedCandidate) {
        chatId = effectiveSelectedCandidate.id
      }

      // If we have a chatId but effectiveSelectedCandidate doesn't match, try to find it in activeChats
      // This handles reconnection cases where EasyRTC ID changed but selectedCandidate wasn't updated
      if (chatId && effectiveSelectedCandidate?.id !== chatId) {
        const activeChat = this.connectionState.activeChats?.find(
          (chat) => chat.id === chatId,
        )
        if (activeChat) {
          effectiveSelectedCandidate = {
            ...activeChat,
            easyrtcid: activeChat.candidateEasyrtcid,
          }
        }
      }

      // If still no chatId, try to get it from activeChats
      if (
        !chatId &&
        this.connectionState.activeChats &&
        this.connectionState.activeChats.length > 0
      ) {
        // For now, assume only one active chat as mentioned in the requirements
        chatId = this.connectionState.activeChats[0].id
        // console.log('CoachChatManager - Using first active chat ID:', chatId)
      }

      if (!chatId && !effectiveSelectedCandidate) {
        throw new Error('No active chat or candidate selected')
      }

      // Find the active chat to get chat slot info
      const activeChat = this.connectionState.activeChats?.find(
        (chat) =>
          chat.id === chatId ||
          chat.candidateEasyrtcid === chatId ||
          chat.candidateEasyrtcid === effectiveSelectedCandidate?.easyrtcid,
      )

      const message = {
        id: Date.now(),
        type: 'text',
        content,
        timestamp: new Date().toISOString(),
        sender: 'coach',
        chatId: chatId || activeChat?.id || effectiveSelectedCandidate?.id,
        chatSlot: activeChat?.chatSlot ?? effectiveSelectedCandidate?.chatSlot,
        chatKey: activeChat?.chatKey ?? effectiveSelectedCandidate?.chatKey,
      }

      // Add message to local messages array (also adds to chat slot history)
      this.addMessage(message)

      // Use activeChat's candidateEasyrtcid if available (handles reconnection cases)
      // Otherwise fall back to effectiveSelectedCandidate
      const candidateEasyrtcid =
        activeChat?.candidateEasyrtcid || effectiveSelectedCandidate?.easyrtcid

      // Send message via EasyRTC
      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc && candidateEasyrtcid) {
        /*
        console.log(
          'CoachChatManager - Sending message via EasyRTC to:',
          candidateEasyrtcid,
        )
        */
        easyrtc.sendData(candidateEasyrtcid, 'message', content)
      } else {
        console.error(
          'CoachChatManager - Cannot send message: easyrtc or candidateEasyrtcid not available',
          {
            hasEasyrtc: !!easyrtc,
            candidateEasyrtcid: candidateEasyrtcid,
            activeChat: activeChat,
            effectiveSelectedCandidate: effectiveSelectedCandidate,
          },
        )
      }

      return message
    } catch (error) {
      console.error('CoachChatManager - Error sending message:', error)
      throw error
    }
  }

  sendTypingIndicator(chatId = null, selectedCandidate = null) {
    try {
      // Check if rtcService is available
      if (!this.rtcService) {
        console.error(
          'CoachChatManager - rtcService is not available for typing indicator',
        )
        return
      }

      // Use provided selectedCandidate, or fall back to connectionState
      const effectiveSelectedCandidate =
        selectedCandidate ||
        this.connectionState.selectedCandidate ||
        this.connectionState.selectedCandidate2

      if (!chatId && effectiveSelectedCandidate) {
        chatId = effectiveSelectedCandidate.id
      }

      if (!chatId && !effectiveSelectedCandidate) {
        // console.log(
        //   'CoachChatManager - Cannot send typing indicator: no chatId or selectedCandidate',
        // )
        return
      }

      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc && effectiveSelectedCandidate?.easyrtcid) {
        // console.log(
        //   'CoachChatManager - Sending typing indicator to candidate:',
        //   effectiveSelectedCandidate.easyrtcid,
        // )
        easyrtc.sendData(effectiveSelectedCandidate.easyrtcid, 'typing', '')
      } else {
        // console.log(
        //   'CoachChatManager - Cannot send typing indicator: EasyRTC not available or no candidate easyrtcid',
        // )
      }
    } catch (error) {
      console.error('CoachChatManager - Error sending typing indicator:', error)
    }
  }

  setCameraReady(ready) {
    // Only set cameraReady to true once, never back to false
    if (ready && !this.connectionState.cameraReady) {
      this.connectionState.cameraReady = true
      // console.log('CoachChatManager - Camera ready set to true')
      this.notifyListeners('stateChange', this.connectionState)
    }
  }

  setAvailability(availability, options = {}) {
    try {
      const { force = false } = options
      // Prevent availability change if coach is in an active chat (unless forced, e.g. logout)
      const activeChats = this.connectionState.activeChats || []
      if (!force && activeChats.length > 0) {
        console.warn(
          'CoachChatManager - Cannot change availability while in an active chat',
        )
        return false
      }

      // Manual dropdown / logout always cancels the post-chat cool-down
      this._clearPostChatAvailabilityCooldown()

      // Update availability state
      this.connectionState.availability = availability
      this.connectionState.isAvailable = availability !== 'away'

      // Update presence in EasyRTC based on availability
      if (this.rtcService) {
        if (availability === 'away') {
          this.rtcService.updateCoachPresence('away', 'Unavailable')
          this.rtcService.updateCurrentUserStatus('away')
          // Drop any ringing requests — coach is no longer accepting (CTS-9831)
          this._declinePendingChatsDueToUnavailability()
        } else {
          // Live preference: apply capacity-aware presence (text_only / audio_video / dnd)
          this._updatePresenceForCapacity()
        }
      }

      // Notify listeners
      this.notifyListeners('availabilityChanged', { availability })
      this.notifyListeners('stateChange', this.connectionState)

      return true
    } catch (error) {
      console.error('CoachChatManager - Error setting availability:', error)
      return false
    }
  }

  // Private helper methods
  _sendChatResponse(candidateEasyrtcid, response) {
    try {
      /*
      console.log('CoachChatManager - _sendChatResponse called with:', {
        candidateEasyrtcid,
        response,
      })
      */

      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc) {
        // Send the appropriate message type that the candidate side expects
        let messageType
        let messageData

        switch (response) {
          case 'accepted':
            messageType = 'call_accepted'
            messageData = this.connectionState.myEasyrtcid
            break
          case 'declined':
            messageType = 'call_rejected'
            messageData = this.connectionState.myEasyrtcid
            break
          case 'missed':
            messageType = 'call_missed'
            messageData = this.connectionState.myEasyrtcid
            break
          default:
            // Fallback to chat_response for other types
            messageType = 'chat_response'
            messageData = {
              response,
              coachId: this.connectionState.myEasyrtcid,
            }
        }

        /*
        console.log('CoachChatManager - About to send message:', {
          messageType,
          messageData,
          to: candidateEasyrtcid,
        })
        */
        easyrtc.sendData(candidateEasyrtcid, messageType, messageData)
        /*
        console.log(
          `CoachChatManager - Sent ${messageType} to candidate:`,
          candidateEasyrtcid,
        )
        */
      } else {
        console.error(
          'CoachChatManager - EasyRTC not available for sending chat response',
        )
      }
    } catch (error) {
      console.error('CoachChatManager - Error sending chat response:', error)
    }
  }

  _sendCoachEndedChatAction(candidateEasyrtcid) {
    try {
      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc) {
        easyrtc.sendData(candidateEasyrtcid, 'coach_action', {
          action_type: 'ended_chat',
        })
      }
    } catch (error) {
      console.error(
        'CoachChatManager - Error sending coach ended_chat action:',
        error,
      )
    }
  }

  _sendChatEnded(candidateEasyrtcid) {
    try {
      const easyrtc = this.rtcService.getEasyRTC()
      if (easyrtc) {
        easyrtc.sendData(
          candidateEasyrtcid,
          'chat_ended',
          this.connectionState.myEasyrtcid,
        )
      }
    } catch (error) {
      console.error('CoachChatManager - Error sending chat ended:', error)
    }
  }

  handleIncomingChatRequest(data) {
    // console.log('[CoachChatManager] handleIncomingChatRequest called:', {
    //   data,
    //   currentActiveChats: this.connectionState.activeChats,
    //   currentPendingChats: this.connectionState.pendingChats,
    //   timestamp: new Date().toISOString(),
    // })

    // Check if this candidate already has an active or pending chat
    // First check by EasyRTC ID (normal case)
    let existingActiveChat = this.connectionState.activeChats?.find(
      (chat) => chat.candidateEasyrtcid === data.candidateEasyrtcid,
    )
    let existingPendingChat = this.connectionState.pendingChats?.find(
      (chat) => chat.candidateEasyrtcid === data.candidateEasyrtcid,
    )

    // If not found by EasyRTC ID but we have a conversationId, check by conversationId
    // This handles reconnection cases where candidate gets a new EasyRTC ID
    if (!existingActiveChat && !existingPendingChat && data.conversationId) {
      existingActiveChat = this.connectionState.activeChats?.find(
        (chat) => chat.conversationId === data.conversationId,
      )
      existingPendingChat = this.connectionState.pendingChats?.find(
        (chat) => chat.conversationId === data.conversationId,
      )

      // If found by conversationId, this is a reconnection - update the EasyRTC ID
      if (existingActiveChat) {
        // Update the existing chat's EasyRTC ID to the new one
        existingActiveChat.candidateEasyrtcid = data.candidateEasyrtcid
        // Notify listeners of the update
        this.notifyListeners('stateChange', this.connectionState)
        // Don't create a new chat - this is a reconnection to existing chat
        return
      }

      if (existingPendingChat) {
        // Update the pending chat's EasyRTC ID to the new one
        existingPendingChat.candidateEasyrtcid = data.candidateEasyrtcid
        // Notify listeners of the update
        this.notifyListeners('stateChange', this.connectionState)
        // Don't create a new chat - this is a reconnection to existing pending chat
        return
      }
    }

    if (existingActiveChat) {
      console.warn(
        '[CoachChatManager] handleIncomingChatRequest - Candidate already has active chat, ignoring:',
        {
          candidateEasyrtcid: data.candidateEasyrtcid,
          activeChat: existingActiveChat,
        },
      )
      return
    }

    if (existingPendingChat) {
      console.warn(
        '[CoachChatManager] handleIncomingChatRequest - Candidate already has pending chat, ignoring:',
        {
          candidateEasyrtcid: data.candidateEasyrtcid,
          pendingChat: existingPendingChat,
        },
      )
      return
    }

    // CTS-9831: Coach preference away — reject new requests authoritatively
    if (!this._isCoachAcceptingNewChats()) {
      this._rejectIncomingCallDueToUnavailability({
        candidateEasyrtcid: data.candidateEasyrtcid,
        conversationId: data.conversationId,
        chatMode: data.chatMode || 'text',
        candidateName: data.candidateName,
        candidateId: data.candidateId || data.candidateEasyrtcid,
      })
      return
    }

    // Check if we recently ended a chat with this candidate (within last 30 seconds)
    // This prevents race condition where chat request arrives after candidate reconnects
    const recentlyEndedChat = this.connectionState.chatHistory?.find((chat) => {
      if (
        chat.candidateEasyrtcid !== data.candidateEasyrtcid ||
        chat.status !== 'ended'
      ) {
        return false
      }

      // Check using timestamp if available, otherwise try to parse endedAt
      const endedTime = chat.endedAtTimestamp
        ? chat.endedAtTimestamp
        : chat.endedAt
          ? new Date(chat.endedAt).getTime()
          : null

      if (!endedTime) {
        return false
      }

      const timeSinceEnded = Date.now() - endedTime
      return timeSinceEnded < 30000 // 30 second cooldown
    })

    if (recentlyEndedChat) {
      const endedTime = recentlyEndedChat.endedAtTimestamp
        ? recentlyEndedChat.endedAtTimestamp
        : recentlyEndedChat.endedAt
          ? new Date(recentlyEndedChat.endedAt).getTime()
          : null

      console.warn(
        '[CoachChatManager] handleIncomingChatRequest - Ignoring chat request from candidate with recently ended chat:',
        {
          candidateEasyrtcid: data.candidateEasyrtcid,
          endedAt: recentlyEndedChat.endedAt,
          endedAtTimestamp: recentlyEndedChat.endedAtTimestamp,
          timeSinceEnded: endedTime ? Date.now() - endedTime : 'unknown',
        },
      )
      return
    }

    const incomingChatMode = data.chatMode || 'text'
    const capacity = this._getIncomingChatCapacity(incomingChatMode)

    console.log(
      '[CoachChatManager] handleIncomingChatRequest - capacity decision:',
      {
        candidateEasyrtcid: data.candidateEasyrtcid,
        conversationId: data.conversationId,
        incomingChatMode,
        capacity,
        activeChatsCount: this.connectionState.activeChats?.length || 0,
        pendingChatsCount: this.connectionState.pendingChats?.length || 0,
      },
    )

    if (capacity.status === 'over_capacity') {
      // No pending chat row exists yet — must notify the candidate directly (handleCallMissed
      // only runs when there is a pending chat).
      const candidateEasyrtcid = data.candidateEasyrtcid
      console.log(
        '[CoachChatManager] handleIncomingChatRequest - over_capacity, sending call_missed:',
        {
          candidateEasyrtcid,
          conversationId: data.conversationId,
          incomingChatMode,
        },
      )
      if (candidateEasyrtcid) {
        this._sendChatResponse(candidateEasyrtcid, 'missed')
        this._recordMissedCallDueToCapacity({
          candidateEasyrtcid,
          conversationId: data.conversationId,
          chatMode: incomingChatMode,
          candidateName: data.candidateName,
          candidateId: data.candidateId,
        })
      }
      return
    }

    const chatSlot = capacity.slot || 1
    const chatKey = `chat-${chatSlot}`

    const easyrtc = this.rtcService.easyrtc
    const candidateEasyrtcid = data.candidateEasyrtcid || data.candidateId
    let browser = data.browser
    let isMobile = data.isMobile
    let locale = data.locale
    if (easyrtc && candidateEasyrtcid) {
      browser =
        browser ||
        easyrtc.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'browser',
        )
      isMobile =
        isMobile ??
        easyrtc.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'isMobile',
        )
      locale =
        locale ||
        easyrtc.getRoomApiField(
          this.connectionState.chatRoom,
          candidateEasyrtcid,
          'userLocale',
        )
    }

    // Initialize chat slot metadata for this pending chat
    const chatSlotData = this.connectionState.chats?.[chatKey]
    const slotHasActiveChat = (this.connectionState.activeChats || []).some(
      (chat) => (chat.chatKey || `chat-${chat.chatSlot || 1}`) === chatKey,
    )
    if (chatSlotData) {
      chatSlotData.candidateId = data.candidateId
      chatSlotData.chatMode = incomingChatMode
      chatSlotData.conversationId = data.conversationId
      // Do not wipe in-progress history when another chat is still active in this slot
      if (!slotHasActiveChat) {
        chatSlotData.chatHistory = {
          messages: [],
          errors: [],
          start_time: null,
          end_time: null,
          chat_type: incomingChatMode,
          coach_token: null,
          candidate_token: null,
        }
      }
    }

    // Add to pending chats
    const pendingChat = {
      id: Date.now(),
      candidateId: data.candidateId,
      candidateName: data.candidateName,
      candidateEasyrtcid: data.candidateEasyrtcid,
      conversationId: data.conversationId,
      chatMode: incomingChatMode,
      chatSlot,
      chatKey,
      browser,
      isMobile,
      locale,
      timestamp: new Date(),
    }

    this.connectionState.pendingChats = [
      ...this.connectionState.pendingChats,
      pendingChat,
    ]

    // Update available chat slot based on new pending chat
    if (incomingChatMode === 'audio' || incomingChatMode === 'video') {
      // Any audio/video pending chat consumes all capacity
      this.connectionState.availableChat = null
    } else {
      // Text chat: if we just filled slot 1, remaining is slot 2; if we filled slot 2, no more capacity
      if (chatSlot === 1) {
        this.connectionState.availableChat = 2
      } else {
        this.connectionState.availableChat = null
      }
    }

    // Update presence based on new capacity (may mark coach as busy/unavailable)
    this._updatePresenceForCapacity()

    console.log(
      '[CoachChatManager] handleIncomingChatRequest - Added to pendingChats:',
      {
        pendingChat,
        pendingChatsAfter: this.connectionState.pendingChats,
        availableChat: this.connectionState.availableChat,
      },
    )

    // Notify listeners
    this.notifyListeners('incomingChatRequest', pendingChat)
    this.notifyListeners('stateChange', this.connectionState)
  }

  handleIncomingCall(data) {
    // Get candidate info from the room using easyrtc directly
    const easyrtc = this.rtcService.easyrtc
    if (!easyrtc) {
      console.error(
        '[CoachChatManager] handleIncomingCall - EasyRTC not available',
      )
      return
    }

    const chatMode = easyrtc.getRoomApiField(
      this.connectionState.chatRoom,
      data.fromEasyrtcid,
      'chatMode',
    )
    const conversationId = easyrtc.getRoomApiField(
      this.connectionState.chatRoom,
      data.fromEasyrtcid,
      'conversationId',
    )
    const candidateName =
      easyrtc.getRoomApiField(
        this.connectionState.chatRoom,
        data.fromEasyrtcid,
        'displayName',
      ) ||
      easyrtc.getRoomApiField(
        this.connectionState.chatRoom,
        data.fromEasyrtcid,
        'username',
      )
    const browser = easyrtc.getRoomApiField(
      this.connectionState.chatRoom,
      data.fromEasyrtcid,
      'browser',
    )
    const isMobile = easyrtc.getRoomApiField(
      this.connectionState.chatRoom,
      data.fromEasyrtcid,
      'isMobile',
    )
    const locale = easyrtc.getRoomApiField(
      this.connectionState.chatRoom,
      data.fromEasyrtcid,
      'userLocale',
    )

    // Get candidate token for caching (will be stored in chat object)
    const candidateToken =
      (easyrtc && easyrtc.idToName(data.fromEasyrtcid)) ||
      easyrtc?.getRoomApiField(
        this.connectionState.chatRoom,
        data.fromEasyrtcid,
        'chatToken',
      ) ||
      data.fromEasyrtcid ||
      ''

    // Check candidate's presence to see if they have "Chat Ended" status
    let candidatePresence = null
    if (easyrtc && this.connectionState.chatRoom) {
      const roomOccupants = easyrtc.getRoomOccupantsAsMap(
        this.connectionState.chatRoom,
      )
      const candidateOccupant = roomOccupants?.[data.fromEasyrtcid]
      candidatePresence = candidateOccupant?.presence
    }

    // console.log(
    //   '[CoachChatManager] handleIncomingCall - Candidate presence check:',
    //   {
    //     candidateEasyrtcid: data.fromEasyrtcid,
    //     presence: candidatePresence,
    //     presenceStatus: candidatePresence?.status,
    //     isChatEnded: candidatePresence?.status === 'Chat Ended',
    //   },
    // )

    // If candidate has "Chat Ended" presence, check if this is a reconnection
    // Allow reconnection if conversationId matches an existing active chat OR a chat in history
    if (candidatePresence?.status === 'Chat Ended') {
      // Check if this conversationId matches an existing active chat (reconnection case)
      const isReconnectionToActiveChat =
        conversationId &&
        this.connectionState.activeChats?.some(
          (chat) => chat.conversationId === conversationId,
        )

      // Also check if it matches a chat in history (candidate reconnecting after chat ended)
      const isReconnectionToHistoryChat =
        conversationId &&
        this.connectionState.chatHistory?.some(
          (chat) => chat.conversationId === conversationId,
        )

      const isReconnection =
        isReconnectionToActiveChat || isReconnectionToHistoryChat

      if (!isReconnection) {
        console.warn(
          '[CoachChatManager] handleIncomingCall - Ignoring incoming call from candidate with Chat Ended presence (not a reconnection):',
          {
            candidateEasyrtcid: data.fromEasyrtcid,
            conversationId: conversationId,
            presence: candidatePresence,
          },
        )
        return
      } else {
        // This is a reconnection - allow it to proceed
      }
    }

    // Check if this candidate already has an active or pending chat
    // First check by EasyRTC ID (normal case)
    let existingActiveChat = this.connectionState.activeChats?.find(
      (chat) => chat.candidateEasyrtcid === data.fromEasyrtcid,
    )
    let _existingPendingChat = this.connectionState.pendingChats?.find(
      (chat) => chat.candidateEasyrtcid === data.fromEasyrtcid,
    )

    // If not found by EasyRTC ID but we have a conversationId, check by conversationId
    // This handles reconnection cases where candidate gets a new EasyRTC ID
    // Also check chatHistory for reconnections after chat ended
    if (!existingActiveChat && !_existingPendingChat && conversationId) {
      existingActiveChat = this.connectionState.activeChats?.find(
        (chat) => chat.conversationId === conversationId,
      )
      _existingPendingChat = this.connectionState.pendingChats?.find(
        (chat) => chat.conversationId === conversationId,
      )

      // If not found in active or pending, check history for reconnection
      // If found in history, we'll create a new pending chat (reconnection after chat ended)
      const existingHistoryChat = this.connectionState.chatHistory?.find(
        (chat) => chat.conversationId === conversationId,
      )
      if (existingHistoryChat) {
        // This is a reconnection after chat ended - allow it to proceed and create new pending chat
        // Don't set existingActiveChat or _existingPendingChat, let it fall through to create new pending
      }

      // If found by conversationId, this is a reconnection - update the EasyRTC ID
      if (existingActiveChat) {
        // Update the existing chat's EasyRTC ID to the new one
        existingActiveChat.candidateEasyrtcid = data.fromEasyrtcid
        // Preserve candidateToken if it exists (for candidate info caching)
        if (!existingActiveChat.candidateToken && candidateToken) {
          existingActiveChat.candidateToken = candidateToken
        }

        // Update chat slot data with new EasyRTC ID
        const chatKey =
          existingActiveChat.chatKey ||
          `chat-${existingActiveChat.chatSlot || 1}`
        const chatSlotData = this.connectionState.chats[chatKey]
        if (chatSlotData) {
          chatSlotData.candidateId = data.fromEasyrtcid
        }

        // Update selectedCandidate or selectedCandidate2 with new EasyRTC ID
        // This is critical for sendMessage to work correctly
        const chatSlot = existingActiveChat.chatSlot || 1
        if (chatSlot === 1) {
          if (this.connectionState.selectedCandidate) {
            this.connectionState.selectedCandidate.candidateEasyrtcid =
              data.fromEasyrtcid
            this.connectionState.selectedCandidate.easyrtcid =
              data.fromEasyrtcid
          }
        } else {
          if (this.connectionState.selectedCandidate2) {
            this.connectionState.selectedCandidate2.candidateEasyrtcid =
              data.fromEasyrtcid
            this.connectionState.selectedCandidate2.easyrtcid =
              data.fromEasyrtcid
          }
        }

        // Auto-accept the reconnection by sending call_accepted message
        // This tells the candidate the chat is still active
        const easyrtc = this.rtcService.getEasyRTC()
        if (easyrtc) {
          // Send call_accepted with conversationId for reconnection
          easyrtc.sendData(
            data.fromEasyrtcid,
            'call_accepted',
            this.connectionState.myEasyrtcid,
            undefined,
            conversationId,
          )
        }

        // Notify listeners of the update
        this.notifyListeners('stateChange', this.connectionState)
        this.notifyListeners('chatReconnected', {
          ...existingActiveChat,
          candidateEasyrtcid: data.fromEasyrtcid,
        })

        // Don't create a new chat - this is a reconnection to existing active chat
        // The chat is already active, so we just update the EasyRTC ID and accept
        return
      }

      if (_existingPendingChat) {
        // Update the pending chat's EasyRTC ID to the new one
        _existingPendingChat.candidateEasyrtcid = data.fromEasyrtcid
        // Notify listeners of the update
        this.notifyListeners('stateChange', this.connectionState)
        // Don't create a new chat - this is a reconnection to existing pending chat
        return
      }
    }

    // console.log(
    //   '[CoachChatManager] handleIncomingCall - Checking for existing chats:',
    //   {
    //     candidateEasyrtcid: data.fromEasyrtcid,
    //     existingActiveChat: !!existingActiveChat,
    //     existingPendingChat: !!existingPendingChat,
    //   },
    // )

    if (existingActiveChat) {
      console.warn(
        '[CoachChatManager] handleIncomingCall - Candidate already has active chat, ignoring incoming call:',
        {
          candidateEasyrtcid: data.fromEasyrtcid,
          activeChat: existingActiveChat,
        },
      )
      return
    }

    // Check if we recently ended a chat with this candidate (within last 5 seconds)
    // This prevents race condition where call_start arrives before presence update
    // Check by both EasyRTC ID and conversationId (for reconnection cases)
    // BUT: Allow reconnections even if recently ended (same conversationId)
    const recentlyEndedChat = this.connectionState.chatHistory?.find((chat) => {
      const matchesId = chat.candidateEasyrtcid === data.fromEasyrtcid
      const matchesConversationId =
        conversationId && chat.conversationId === conversationId
      const isRecentlyEnded =
        chat.status === 'ended' &&
        chat.endedAt &&
        Date.now() - new Date(chat.endedAt).getTime() < 5000 // 5 second cooldown

      // If it's a reconnection (same conversationId), don't block it even if recently ended
      if (matchesConversationId) {
        return false // Always allow reconnection by conversationId
      }

      // Only block if it matches by EasyRTC ID (not a reconnection)
      return matchesId && isRecentlyEnded
    })

    if (recentlyEndedChat) {
      console.warn(
        '[CoachChatManager] handleIncomingCall - Ignoring incoming call from candidate with recently ended chat (not a reconnection):',
        {
          candidateEasyrtcid: data.fromEasyrtcid,
          conversationId: conversationId,
          endedAt: recentlyEndedChat.endedAt,
          timeSinceEnded:
            Date.now() - new Date(recentlyEndedChat.endedAt).getTime(),
        },
      )
      return
    }

    // CTS-9831: Prefer intentional away — reject new chats even if candidate UI is stale
    if (!this._isCoachAcceptingNewChats()) {
      this._rejectIncomingCallDueToUnavailability({
        candidateEasyrtcid: data.fromEasyrtcid,
        conversationId,
        chatMode: chatMode || 'text',
        candidateName: candidateName || 'Unknown',
        candidateId: data.fromEasyrtcid,
      })
      return
    }

    const incomingChatMode = chatMode || 'text'
    const capacity = this._getIncomingChatCapacity(incomingChatMode)

    console.log('[CoachChatManager] handleIncomingCall - capacity decision:', {
      candidateEasyrtcid: data.fromEasyrtcid,
      conversationId,
      incomingChatMode,
      capacity,
      activeChatsCount: this.connectionState.activeChats?.length || 0,
      pendingChatsCount: this.connectionState.pendingChats?.length || 0,
    })

    if (capacity.status === 'over_capacity') {
      const candidateEasyrtcid = data.fromEasyrtcid || data.candidateEasyrtcid
      console.log(
        '[CoachChatManager] handleIncomingCall - over_capacity, sending call_missed:',
        {
          candidateEasyrtcid,
          conversationId,
          incomingChatMode,
        },
      )
      if (candidateEasyrtcid) {
        this._sendChatResponse(candidateEasyrtcid, 'missed')
        this._recordMissedCallDueToCapacity({
          candidateEasyrtcid,
          conversationId,
          chatMode: incomingChatMode,
          candidateName: candidateName || 'Unknown',
          candidateId: data.fromEasyrtcid,
        })
      }
      return
    }

    const chatSlot = capacity.slot || 1
    const chatKey = `chat-${chatSlot}`

    // Initialize chat slot metadata for this pending call
    const chatSlotData = this.connectionState.chats?.[chatKey]
    if (chatSlotData) {
      chatSlotData.candidateId = data.fromEasyrtcid
      chatSlotData.chatMode = incomingChatMode
      chatSlotData.conversationId = conversationId
      chatSlotData.chatHistory = {
        messages: [],
        errors: [],
        start_time: null,
        end_time: null,
        chat_type: incomingChatMode,
        coach_token: null,
        candidate_token: candidateToken || null,
      }
    }

    // Add to pending chats
    // Note: candidateToken is already declared earlier in this function (line 1689)
    const pendingChat = {
      id: Date.now(),
      candidateId: data.fromEasyrtcid,
      candidateName: candidateName || 'Unknown',
      candidateEasyrtcid: data.fromEasyrtcid,
      conversationId: conversationId,
      chatMode: incomingChatMode,
      chatSlot,
      chatKey,
      browser: browser,
      isMobile: isMobile,
      locale: locale,
      timestamp: new Date(),
      acceptCallback: data.acceptCallback,
      rejectCallback: data.rejectCallback,
      candidateToken: candidateToken, // Store candidateToken for caching
    }

    this.connectionState.pendingChats = [
      ...this.connectionState.pendingChats,
      pendingChat,
    ]

    // Update available chat slot based on new pending call
    if (incomingChatMode === 'audio' || incomingChatMode === 'video') {
      // Any audio/video pending chat consumes all capacity
      this.connectionState.availableChat = null
    } else {
      // Text chat: if we just filled slot 1, remaining is slot 2; if we filled slot 2, no more capacity
      if (chatSlot === 1) {
        this.connectionState.availableChat = 2
      } else {
        this.connectionState.availableChat = null
      }
    }

    // Update presence based on new capacity (may mark coach as busy/unavailable)
    this._updatePresenceForCapacity()

    console.log(
      '[CoachChatManager] handleIncomingCall - Added to pendingChats:',
      {
        pendingChat,
        pendingChatsAfter: this.connectionState.pendingChats,
        availableChat: this.connectionState.availableChat,
      },
    )

    // Notify listeners
    this.notifyListeners('incomingChatRequest', pendingChat)
    this.notifyListeners('stateChange', this.connectionState)
  }

  handleEasyrtcCall(data) {
    // console.log('CoachChatManager - Handling easyrtcCall event:', data)
    const candidateEasyrtcid = data.fromEasyrtcid

    // Check if the candidate is already in an active chat
    const activeChat = this.connectionState.activeChats?.find(
      (chat) => chat.candidateEasyrtcid === candidateEasyrtcid,
    )

    if (activeChat) {
      /*
      console.log(
        'CoachChatManager - Candidate already in active chat, auto-accepting EasyRTC call:',
        candidateEasyrtcid,
      )
      */

      // Initialize media for video and audio calls before accepting
      if (activeChat.chatMode === 'video') {
        this.initializeCoachMediaForVideoCall(activeChat)
      } else if (activeChat.chatMode === 'audio') {
        this.initializeCoachMediaForAudioCall(activeChat)
      }

      // Auto-accept the EasyRTC call since we already agreed to chat
      // Accept the call - old EasyRTC library only accepts true/false
      data.acceptCallback(true)
    } else {
      /*
      console.log(
        'CoachChatManager - Candidate not in active chat, rejecting EasyRTC call:',
        candidateEasyrtcid,
      )
      */
      // Reject the call since we haven't agreed to chat yet
      data.acceptCallback(false)
    }
  }

  handleChatResponse(data) {
    if (data.response === 'accepted') {
      // Candidate accepted our chat request
      this.connectionState.chatStatus = 'chat_active'
      this.notifyListeners('stateChange', this.connectionState)
    } else if (data.response === 'declined') {
      // Candidate declined our chat request
      this.connectionState.chatStatus = 'chat_declined'
      this.notifyListeners('stateChange', this.connectionState)
    }
  }

  /**
   * When candidate's WebRTC stream closes (e.g. mobile lock, network drop), treat as "candidate left":
   * add "Candidate has left the chat" message and end the chat slot.
   */
  handleCandidateStreamClosed(data) {
    try {
      const easyrtcid = data?.easyrtcid
      if (!easyrtcid) return

      const activeChat = this.connectionState.activeChats?.find(
        (chat) => chat.candidateEasyrtcid === easyrtcid,
      )
      if (!activeChat) return

      const previousActiveCount = (this.connectionState.activeChats || [])
        .length
      const chatKey = activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`

      // Add synthetic "Candidate has left the chat" message (same shape as left_chat candidate_action)
      const leftChatMessage = {
        id: Date.now(),
        type: 'candidate_action',
        actionType: 'left_chat',
        actionData: { action_type: 'left_chat' },
        text: 'Candidate has left the chat',
        timestamp: new Date().toISOString(),
        sender: 'candidate',
        chatKey,
        chatId: activeChat.id,
        fromEasyrtcid: easyrtcid,
      }
      this.addMessage(leftChatMessage)

      // End the chat slot (same cleanup as handleChatEndedByCandidate)
      const chatSlotData = this.connectionState.chats[chatKey]
      if (chatSlotData) {
        chatSlotData.chatHistory.end_time = Date.now()
        const chatInfo = {
          messages: chatSlotData.chatHistory.messages || [],
          start_time: chatSlotData.chatHistory.start_time,
          end_time: chatSlotData.chatHistory.end_time,
          chat_type:
            chatSlotData.chatHistory.chat_type ||
            activeChat.chatMode ||
            chatSlotData.chatMode ||
            'text',
          coach_token: chatSlotData.chatHistory.coach_token || '',
          candidate_token: chatSlotData.chatHistory.candidate_token || '',
          conversation_id: chatSlotData.conversationId || '',
        }
        this._submitChatHistory(chatInfo)
        chatSlotData.candidateId = ''
        chatSlotData.chatMode = ''
        chatSlotData.conversationId = ''
        chatSlotData.chatHistory = {
          messages: [],
          errors: [],
          start_time: null,
          end_time: null,
          chat_type: null,
          coach_token: null,
          candidate_token: null,
        }
      }

      this.connectionState.activeChats =
        this.connectionState.activeChats.filter(
          (chat) => chat.id !== activeChat.id,
        )
      this.connectionState.chatHistory = [
        ...(this.connectionState.chatHistory || []),
        {
          ...activeChat,
          status: 'ended',
          endedAt: new Date(),
          endedAtTimestamp: Date.now(),
        },
      ]

      const chatSlot = activeChat.chatSlot || 1
      if (chatSlot === 1) {
        if (
          this.connectionState.selectedCandidate?.id === activeChat.id ||
          this.connectionState.selectedCandidate?.candidateEasyrtcid ===
            activeChat.candidateEasyrtcid
        ) {
          this.connectionState.selectedCandidate = null
        }
      } else {
        if (
          this.connectionState.selectedCandidate2?.id === activeChat.id ||
          this.connectionState.selectedCandidate2?.candidateEasyrtcid ===
            activeChat.candidateEasyrtcid
        ) {
          this.connectionState.selectedCandidate2 = null
        }
      }

      this.connectionState.pendingChats =
        this.connectionState.pendingChats.filter(
          (chat) =>
            chat.candidateId !== activeChat.candidateId &&
            chat.candidateEasyrtcid !== activeChat.candidateEasyrtcid &&
            chat.id !== activeChat.id,
        )
      this._removeMessagesForEndedChat(activeChat)
      this._syncAvailableChatFromOccupancy()
      this._releaseChatMediaIfNeeded(activeChat)
      this._onActiveChatRemoved(previousActiveCount)
      this.notifyListeners('chatEnded', activeChat)
      this.notifyListeners('stateChange', this.connectionState)
    } catch (error) {
      console.error(
        'CoachChatManager - Error handling candidate stream closed:',
        error,
      )
    }
  }

  handleChatEndedByCandidate(data) {
    try {
      // console.log('[CoachChatManager] handleChatEndedByCandidate called:', {
      //   data,
      //   activeChats: this.connectionState.activeChats,
      //   pendingChats: this.connectionState.pendingChats,
      //   timestamp: new Date().toISOString(),
      // })

      // Find the active chat
      const activeChat = this.connectionState.activeChats?.find(
        (chat) => chat.candidateEasyrtcid === data.candidateEasyrtcid,
      )

      // Also check pendingChats in case the chat was never fully accepted
      const candidateEasyrtcid = data.candidateEasyrtcid || data.fromEasyrtcid

      // console.log(
      //   '[CoachChatManager] handleChatEndedByCandidate - Found activeChat:',
      //   {
      //     activeChat,
      //     candidateEasyrtcid,
      //     activeChatsCount: this.connectionState.activeChats?.length,
      //     pendingChatsCount: this.connectionState.pendingChats?.length,
      //   },
      // )

      if (activeChat) {
        const previousActiveCount = (this.connectionState.activeChats || [])
          .length

        // Get chat slot data
        const chatKey = activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`
        const chatSlotData = this.connectionState.chats[chatKey]

        if (chatSlotData) {
          // Update chat history with end time
          chatSlotData.chatHistory.end_time = Date.now()

          // Build chat history JSON for backend submission
          // CTS-9775: fall back to activeChat.chatMode so video/audio rows keep the correct type
          const chatInfo = {
            messages: chatSlotData.chatHistory.messages || [],
            start_time: chatSlotData.chatHistory.start_time,
            end_time: chatSlotData.chatHistory.end_time,
            chat_type:
              chatSlotData.chatHistory.chat_type ||
              activeChat.chatMode ||
              chatSlotData.chatMode ||
              'text',
            coach_token: chatSlotData.chatHistory.coach_token || '',
            candidate_token: chatSlotData.chatHistory.candidate_token || '',
            conversation_id: chatSlotData.conversationId || '',
          }

          // Submit chat history to backend
          this._submitChatHistory(chatInfo)

          // Clear chat slot
          chatSlotData.candidateId = ''
          chatSlotData.chatMode = ''
          chatSlotData.conversationId = ''
          chatSlotData.chatHistory = {
            messages: [],
            errors: [],
            start_time: null,
            end_time: null,
            chat_type: null,
            coach_token: null,
            candidate_token: null,
          }
        }

        // Move from active to history
        this.connectionState.activeChats =
          this.connectionState.activeChats.filter(
            (chat) => chat.id !== activeChat.id,
          )
        this.connectionState.chatHistory = [
          ...(this.connectionState.chatHistory || []),
          {
            ...activeChat,
            status: 'ended',
            endedAt: new Date(),
            endedAtTimestamp: Date.now(), // Store timestamp for easier comparison
          },
        ]

        // Clear selected candidate if it was this one (check both slots)
        const chatSlot = activeChat.chatSlot || 1
        if (chatSlot === 1) {
          if (
            this.connectionState.selectedCandidate?.id === activeChat.id ||
            this.connectionState.selectedCandidate?.candidateEasyrtcid ===
              activeChat.candidateEasyrtcid
          ) {
            this.connectionState.selectedCandidate = null
          }
        } else {
          if (
            this.connectionState.selectedCandidate2?.id === activeChat.id ||
            this.connectionState.selectedCandidate2?.candidateEasyrtcid ===
              activeChat.candidateEasyrtcid
          ) {
            this.connectionState.selectedCandidate2 = null
          }
        }

        // Also remove any pending chats for this candidate to prevent showing accept/reject UI
        const _pendingChatsBefore = [...this.connectionState.pendingChats]
        this.connectionState.pendingChats =
          this.connectionState.pendingChats.filter(
            (chat) =>
              chat.candidateId !== activeChat.candidateId &&
              chat.candidateEasyrtcid !== activeChat.candidateEasyrtcid &&
              chat.id !== activeChat.id,
          )

        // console.log(
        //   '[CoachChatManager] handleChatEndedByCandidate - Cleaned up pendingChats:',
        //   {
        //     before: pendingChatsBefore,
        //     after: this.connectionState.pendingChats,
        //     removedCount:
        //       pendingChatsBefore.length -
        //       this.connectionState.pendingChats.length,
        //   },
        // )

        this._removeMessagesForEndedChat(activeChat)

        this._syncAvailableChatFromOccupancy()

        this._releaseChatMediaIfNeeded(activeChat)

        // Don't set chatStatus here - let getConnectionState compute it
        // This will automatically set it to 'waiting_for_candidate' since activeChats is now empty

        this._onActiveChatRemoved(previousActiveCount)

        // console.log(
        //   '[CoachChatManager] handleChatEndedByCandidate - Final state:',
        //   {
        //     activeChats: this.connectionState.activeChats,
        //     pendingChats: this.connectionState.pendingChats,
        //     selectedCandidate: this.connectionState.selectedCandidate,
        //     chatStatus: this.getConnectionState().chatStatus,
        //     chatHistoryCount: this.connectionState.chatHistory?.length,
        //     lastEndedChat:
        //       this.connectionState.chatHistory?.[
        //         this.connectionState.chatHistory.length - 1
        //       ],
        //   },
        // )

        // Notify listeners
        this.notifyListeners('chatEnded', activeChat)
        this.notifyListeners('stateChange', this.connectionState)
      } else if (candidateEasyrtcid) {
        // If no active chat found, still clean up any pending chats for this candidate
        // This handles edge cases where the chat ended before it was fully accepted
        const _pendingChatsBefore = [...this.connectionState.pendingChats]
        this.connectionState.pendingChats =
          this.connectionState.pendingChats.filter(
            (chat) =>
              chat.candidateId !== candidateEasyrtcid &&
              chat.candidateEasyrtcid !== candidateEasyrtcid,
          )

        // console.log(
        //   '[CoachChatManager] handleChatEndedByCandidate - No active chat found, cleaned pendingChats:',
        //   {
        //     candidateEasyrtcid,
        //     before: pendingChatsBefore,
        //     after: this.connectionState.pendingChats,
        //     removedCount:
        //       pendingChatsBefore.length -
        //       this.connectionState.pendingChats.length,
        //   },
        // )

        // Notify listeners of state change even if no active chat was found
        this.notifyListeners('stateChange', this.connectionState)
      } else {
        console.warn(
          '[CoachChatManager] handleChatEndedByCandidate - No candidateEasyrtcid found in data:',
          data,
        )
      }
    } catch (error) {
      console.error(
        'CoachChatManager - Error handling chat ended by candidate:',
        error,
      )
    }
  }

  handleChatEndedByCoach(data) {
    try {
      // Find the active chat
      const activeChat = this.connectionState.activeChats?.find(
        (chat) => chat.candidateEasyrtcid === data.fromEasyrtcid,
      )

      if (activeChat) {
        const previousActiveCount = (this.connectionState.activeChats || [])
          .length

        // Get chat slot data
        const chatKey = activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`
        const chatSlotData = this.connectionState.chats[chatKey]

        if (chatSlotData) {
          // Update chat history with end time
          chatSlotData.chatHistory.end_time = Date.now()

          // Build chat history JSON for backend submission
          // CTS-9775: fall back to activeChat.chatMode so video/audio rows keep the correct type
          const chatInfo = {
            messages: chatSlotData.chatHistory.messages || [],
            start_time: chatSlotData.chatHistory.start_time,
            end_time: chatSlotData.chatHistory.end_time,
            chat_type:
              chatSlotData.chatHistory.chat_type ||
              activeChat.chatMode ||
              chatSlotData.chatMode ||
              'text',
            coach_token: chatSlotData.chatHistory.coach_token || '',
            candidate_token: chatSlotData.chatHistory.candidate_token || '',
            conversation_id: chatSlotData.conversationId || '',
          }

          // Submit chat history to backend
          this._submitChatHistory(chatInfo)

          // Clear chat slot
          chatSlotData.candidateId = ''
          chatSlotData.chatMode = ''
          chatSlotData.conversationId = ''
          chatSlotData.chatHistory = {
            messages: [],
            errors: [],
            start_time: null,
            end_time: null,
            chat_type: null,
            coach_token: null,
            candidate_token: null,
          }
        }

        // Move from active chat to history
        this.connectionState.activeChats =
          this.connectionState.activeChats.filter(
            (chat) => chat.id !== activeChat.id,
          )
        this.connectionState.chatHistory = [
          ...(this.connectionState.chatHistory || []),
          { ...activeChat, status: 'ended', endedAt: new Date() },
        ]

        // Clear selected candidate if it was this one (check both slots)
        const chatSlot = activeChat.chatSlot || 1
        if (chatSlot === 1) {
          if (
            this.connectionState.selectedCandidate?.id === activeChat.id ||
            this.connectionState.selectedCandidate?.candidateEasyrtcid ===
              activeChat.candidateEasyrtcid
          ) {
            this.connectionState.selectedCandidate = null
          }
        } else {
          if (
            this.connectionState.selectedCandidate2?.id === activeChat.id ||
            this.connectionState.selectedCandidate2?.candidateEasyrtcid ===
              activeChat.candidateEasyrtcid
          ) {
            this.connectionState.selectedCandidate2 = null
          }
        }

        this._removeMessagesForEndedChat(activeChat)

        this._syncAvailableChatFromOccupancy()

        this._releaseChatMediaIfNeeded(activeChat)

        // Don't set chatStatus here - let getConnectionState compute it
        // This will automatically set it to 'waiting_for_candidate' since activeChats is now empty

        this._onActiveChatRemoved(previousActiveCount)

        // Notify listeners
        this.notifyListeners('chatEnded', activeChat)
        this.notifyListeners('stateChange', this.connectionState)
      }
    } catch (error) {
      console.error(
        'CoachChatManager - Error handling chat ended by coach:',
        error,
      )
    }
  }

  // Initialize coach media for video calls
  async initializeCoachMediaForVideoCall(activeChat) {
    try {
      /*
      console.log(
        'CoachChatManager - Initializing coach media for video call:',
        activeChat,
      )
      */

      const easyrtc = this.rtcService.getEasyRTC()
      if (!easyrtc) {
        console.error(
          'CoachChatManager - EasyRTC not available for media initialization',
        )
        return false
      }

      // Enable video and audio for the coach
      easyrtc.enableVideo(true)
      easyrtc.enableAudio(true)
      easyrtc.setAutoInitUserMedia(true)

      // Initialize media source
      easyrtc.initMediaSource(
        (mediastream) => {
          /*
          console.log(
            'CoachChatManager - Coach media source initialized successfully',
          )
          */

          // Store the local stream
          this.connectionState.localStream = mediastream
          this.connectionState.hasLocalVideo = true
          this.connectionState.hasLocalAudio = true
          // CTS-9777: sync to MediaService so pause/mute can toggle tracks
          if (this.mediaService) {
            this.mediaService.setLocalStream(mediastream)
          }

          // Set camera ready when we have a video stream
          if (mediastream && mediastream.getVideoTracks().length > 0) {
            this.setCameraReady(true)
          }

          // Set up self video element using ref
          if (this.videoRefs.selfVideo && mediastream) {
            easyrtc.setVideoObjectSrc(
              this.videoRefs.selfVideo,
              mediastream,
              activeChat.conversationId,
            )
            this.videoRefs.selfVideo.muted = true
            this.videoRefs.selfVideo.style.display = 'block'
            // Preserve mirroring styles for coach's self video
            this.videoRefs.selfVideo.style.transform = 'scaleX(-1)'
          }

          // Notify listeners that local video is ready
          this.notifyListeners('localVideoReady', {
            stream: mediastream,
            hasLocalVideo: true,
            hasLocalAudio: true,
          })
          this.notifyListeners('stateChange', this.connectionState)
        },
        (errorCode, errorDesc) => {
          console.error(
            'CoachChatManager - Coach media initialization failed:',
            errorCode,
            errorDesc,
          )
          // Continue with call even if video fails
        },
        undefined,
        activeChat.conversationId,
      )

      return true
    } catch (error) {
      console.error('CoachChatManager - Error initializing coach media:', error)
      return false
    }
  }

  // Initialize coach media for audio calls
  async initializeCoachMediaForAudioCall(activeChat) {
    try {
      /*
      console.log(
        'CoachChatManager - Initializing coach media for audio call:',
        activeChat,
      )
      */

      const easyrtc = this.rtcService.getEasyRTC()
      if (!easyrtc) {
        console.error(
          'CoachChatManager - EasyRTC not available for media initialization',
        )
        return false
      }

      // Enable audio only for the coach (no video)
      easyrtc.enableVideo(false)
      easyrtc.enableAudio(true)
      easyrtc.setAutoInitUserMedia(true)

      // Initialize media source
      easyrtc.initMediaSource(
        (mediastream) => {
          /*
          console.log(
            'CoachChatManager - Coach audio media source initialized successfully',
          )
          */

          // Store the local stream
          this.connectionState.localStream = mediastream
          this.connectionState.hasLocalVideo = false
          this.connectionState.hasLocalAudio = true
          // CTS-9777: sync to MediaService so mute can toggle tracks
          if (this.mediaService) {
            this.mediaService.setLocalStream(mediastream)
          }

          // Set up self video element (for avatar display) using ref
          if (this.videoRefs.selfVideo && mediastream) {
            easyrtc.setVideoObjectSrc(
              this.videoRefs.selfVideo,
              mediastream,
              activeChat.conversationId,
            )
            this.videoRefs.selfVideo.muted = true
            this.videoRefs.selfVideo.style.display = 'block'
          }

          // Notify listeners that local audio is ready
          this.notifyListeners('localAudioReady', {
            stream: mediastream,
            hasLocalVideo: false,
            hasLocalAudio: true,
          })
          this.notifyListeners('stateChange', this.connectionState)
        },
        (errorCode, errorDesc) => {
          console.error(
            'CoachChatManager - Coach audio media initialization failed:',
            errorCode,
            errorDesc,
          )
          // Continue with call even if audio fails
        },
        undefined,
        activeChat.conversationId,
      )

      return true
    } catch (error) {
      console.error(
        'CoachChatManager - Error initializing coach audio media:',
        error,
      )
      return false
    }
  }

  _shouldReleaseMediaAfterEndingChat(endedChat) {
    const endedMode = endedChat?.chatMode || 'text'
    if (endedMode !== 'video' && endedMode !== 'audio') {
      return false
    }

    const remainingActiveChats = (
      this.connectionState.activeChats || []
    ).filter(
      (chat) =>
        chat.id !== endedChat.id &&
        chat.candidateEasyrtcid !== endedChat.candidateEasyrtcid,
    )

    return !remainingActiveChats.some(
      (chat) => chat.chatMode === 'video' || chat.chatMode === 'audio',
    )
  }

  _releaseChatMediaIfNeeded(endedChat) {
    if (!this._shouldReleaseMediaAfterEndingChat(endedChat)) {
      return
    }

    const chatKey = endedChat.chatKey || `chat-${endedChat.chatSlot || 1}`
    const conversationId =
      endedChat.conversationId ||
      this.connectionState.chats[chatKey]?.conversationId

    this._releaseChatMedia(conversationId)
  }

  _releaseChatMedia(conversationId = null) {
    this.rtcService.releaseLocalMedia({
      conversationId,
      selfVideo: this.videoRefs.selfVideo,
      callerVideo: this.videoRefs.callerVideo,
    })

    this.mediaService.releaseLocalMedia([
      this.connectionState.localStream,
      this.callService.localStream,
      this.rtcService.getLocalStream(),
    ])
    this.callService.releaseLocalMedia()

    this.connectionState.localStream = null
    this.connectionState.remoteStream = null
    this.connectionState.hasLocalVideo = false
    this.connectionState.hasLocalAudio = false
    this.connectionState.hasRemoteVideo = false
    this.connectionState.hasRemoteAudio = false
    this.connectionState.cameraReady = false
  }

  // Media control methods
  // CTS-9777: ensure MediaService has the local stream before toggling tracks
  _ensureMediaServiceLocalStream() {
    if (this.mediaService?.getLocalStream?.()) {
      return true
    }
    const stream =
      this.connectionState.localStream || this.callService?.localStream
    if (stream && this.mediaService) {
      this.mediaService.setLocalStream(stream)
      return true
    }
    return false
  }

  pauseChat() {
    try {
      this._ensureMediaServiceLocalStream()
      const mediaState = this.mediaService.getMediaState()
      if (mediaState.videoPaused) {
        return true
      }
      return this.mediaService.toggleVideoPause()
    } catch (error) {
      console.error('CoachChatManager - Error pausing chat:', error)
      return false
    }
  }

  unpauseChat() {
    try {
      this._ensureMediaServiceLocalStream()
      const mediaState = this.mediaService.getMediaState()
      if (!mediaState.videoPaused) {
        return true
      }
      return this.mediaService.toggleVideoPause()
    } catch (error) {
      console.error('CoachChatManager - Error unpausing chat:', error)
      return false
    }
  }

  muteChat() {
    try {
      this._ensureMediaServiceLocalStream()
      const mediaState = this.mediaService.getMediaState()
      if (mediaState.audioMuted) {
        return true
      }
      return this.mediaService.toggleAudioMute()
    } catch (error) {
      console.error('CoachChatManager - Error muting chat:', error)
      return false
    }
  }

  unmuteChat() {
    try {
      this._ensureMediaServiceLocalStream()
      const mediaState = this.mediaService.getMediaState()
      if (!mediaState.audioMuted) {
        return true
      }
      return this.mediaService.toggleAudioMute()
    } catch (error) {
      console.error('CoachChatManager - Error unmuting chat:', error)
      return false
    }
  }

  // Video element ref management
  setVideoRefs(selfVideoRef, callerVideoRef) {
    this.videoRefs.selfVideo = selfVideoRef
    this.videoRefs.callerVideo = callerVideoRef
    /*
    console.log('CoachChatManager - Video refs set:', {
      selfVideo: !!selfVideoRef,
      callerVideo: !!callerVideoRef,
    })
    */

    // Also set the refs in the media service
    if (this.mediaService) {
      this.mediaService.setVideoRefs(selfVideoRef, callerVideoRef)
    }
  }

  setSelfVideoRef(selfVideoRef) {
    this.videoRefs.selfVideo = selfVideoRef
    // console.log('CoachChatManager - Self video ref set:', !!selfVideoRef)

    // Also set the ref in the media service
    if (this.mediaService) {
      this.mediaService.setSelfVideoRef(selfVideoRef)
    }
  }

  setCallerVideoRef(callerVideoRef) {
    this.videoRefs.callerVideo = callerVideoRef
    // console.log('CoachChatManager - Caller video ref set:', !!callerVideoRef)

    // Also set the ref in the media service
    if (this.mediaService) {
      this.mediaService.setCallerVideoRef(callerVideoRef)
    }
  }

  // Utility methods
  disconnect() {
    this._clearPostChatAvailabilityCooldown()
    this.rtcService.disconnect()
    this.callService.cleanup()
    this.connectionState.isConnected = false
    this.notifyListeners('disconnected', this.connectionState)
  }

  isConnected() {
    return this.connectionState.isConnected
  }

  getConnectionState() {
    // Safety check - ensure connectionState exists
    if (!this.connectionState) {
      console.error(
        'CoachChatManager - connectionState is undefined in getConnectionState, reinitializing...',
      )
      this.connectionState = {
        isConnected: false,
        isCoach: true,
        isAvailable: false,
        availability: 'away',
        videoEnabled: false,
        candidates: [],
        selectedCandidate: null,
        selectedCandidate2: null,
        activeChats: [],
        pendingChats: [],
        chatHistory: [],
        chatRoom: null,
        myEasyrtcid: null,
        messages: [],
        chatMode: 'text',
        chatStatus: 'disconnected',
        hasLocalVideo: false,
        hasLocalAudio: false,
        hasRemoteVideo: false,
        hasRemoteAudio: false,
        localStream: null,
        remoteStream: null,
      }
    }

    // Compute chatStatus based on current state
    const state = { ...this.connectionState }

    // Compute chatStatus based on current state - don't preserve 'chat_ended' status
    if (!state.isConnected) {
      state.chatStatus = 'disconnected'
    } else if (state.pendingChats.length > 0) {
      state.chatStatus = 'waiting_for_coach'
    } else if (state.activeChats.length > 0) {
      state.chatStatus = 'chat_active'
    } else {
      state.chatStatus = 'waiting_for_candidate'
    }

    // Include media control states for header mute/pause UI (CC-UX-012)
    const mediaState = this.mediaService?.getMediaState?.()
    if (mediaState) {
      state.audioMuted = mediaState.audioMuted
      state.videoPaused = mediaState.videoPaused
    }

    return state
  }

  _looksLikeUuid(str) {
    if (!str || typeof str !== 'string') {
      return false
    }
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      str.trim(),
    )
  }

  /**
   * Keep easyrtcid -> chat token mapping current so cache lookups work after reconnects.
   */
  _syncEasyrtcidToChatTokenMappings(candidates) {
    if (!candidates?.length) {
      return
    }
    candidates.forEach((c) => {
      if (c.username && c.easyrtcid) {
        this.candidateInfoByEasyrtcid[c.easyrtcid] = c.username
      }
    })
  }

  /**
   * When the room still has a readable display name, merge into cache so we keep it if the room later only exposes the token.
   */
  _seedCandidateDisplayCacheFromRoomCandidates(candidates) {
    if (!candidates?.length) {
      return
    }
    this._syncEasyrtcidToChatTokenMappings(candidates)
    candidates.forEach((c) => {
      const chatToken = c.username
      if (!chatToken || !c.easyrtcid) {
        return
      }
      const roomName = c.displayName
      if (
        !roomName ||
        this._looksLikeUuid(roomName) ||
        roomName === chatToken
      ) {
        return
      }
      const existing = this.candidateInfo[chatToken] || {}
      const existingName = existing.name || existing.displayName
      if (
        existingName &&
        !this._looksLikeUuid(String(existingName)) &&
        existingName !== chatToken
      ) {
        return
      }
      this.candidateInfo[chatToken] = {
        ...existing,
        name: roomName,
        displayName: roomName,
      }
    })
  }

  _linkCandidateCacheToEasyrtcid(chatToken) {
    if (!chatToken) {
      return
    }
    const match = this.connectionState.candidates?.find(
      (c) => c.username === chatToken,
    )
    if (match?.easyrtcid) {
      this.candidateInfoByEasyrtcid[match.easyrtcid] = chatToken
    }
  }

  _refreshCandidatesWithCachedNames() {
    if (!this.connectionState.candidates?.length) {
      return
    }
    this.connectionState.candidates = this.applyCachedDisplayNamesToCandidates(
      this.connectionState.candidates,
    )
    this.notifyListeners('candidatesUpdate', this.connectionState.candidates)
    this.notifyListeners('stateChange', this.connectionState)
  }

  /**
   * Merge API / cache display names into room candidate rows for list UI.
   */
  applyCachedDisplayNamesToCandidates(candidates) {
    if (!candidates?.length) {
      return candidates || []
    }
    return candidates.map((c) => {
      const displayName = this.resolveCandidateDisplayName(c)
      const firstName = this.resolveCandidateFirstName(c)
      const candidateId = this.resolveCandidateId(c)
      const programLevel = this.resolveCandidateProgramLevel(c)
      return { ...c, displayName, firstName, candidateId, programLevel }
    })
  }

  resolveCandidateId(candidate) {
    if (!candidate) {
      return null
    }

    const fromPresence = candidate.candidateId
    if (fromPresence) {
      return fromPresence
    }

    const chatToken =
      candidate.username ||
      this.candidateInfoByEasyrtcid[candidate.easyrtcid] ||
      ''
    const info = chatToken ? this.candidateInfo[chatToken] : null
    return info?.id || null
  }

  resolveCandidateProgramLevel(candidate) {
    if (!candidate) {
      return null
    }

    const chatToken =
      candidate.username ||
      this.candidateInfoByEasyrtcid[candidate.easyrtcid] ||
      ''
    const info = chatToken ? this.candidateInfo[chatToken] : null
    return info?.program_level || null
  }

  resolveCandidateFirstName(candidate) {
    if (!candidate) {
      return ''
    }
    const chatToken =
      candidate.username ||
      this.candidateInfoByEasyrtcid[candidate.easyrtcid] ||
      ''
    const info = chatToken ? this.candidateInfo[chatToken] : null
    const fromCache = info?.first_name || info?.firstName
    if (fromCache && !this._looksLikeUuid(String(fromCache))) {
      return fromCache
    }
    const roomFirst = candidate.firstName
    if (roomFirst && !this._looksLikeUuid(String(roomFirst))) {
      return roomFirst
    }
    const full = this.resolveCandidateDisplayName(candidate)
    if (full && full !== chatToken) {
      const part = String(full).split(/\s+/)[0]
      if (part && !this._looksLikeUuid(part)) {
        return part
      }
    }
    return roomFirst || chatToken || ''
  }

  /**
   * Prefer cached API name (keyed by chat token or easyrtcid), then readable room fields, then token.
   */
  resolveCandidateDisplayName(candidate) {
    if (!candidate) {
      return ''
    }
    const chatToken =
      candidate.username ||
      this.candidateInfoByEasyrtcid[candidate.easyrtcid] ||
      ''
    const info = chatToken ? this.candidateInfo[chatToken] : null

    const nameFromCache = info?.name || info?.displayName
    if (nameFromCache && !this._looksLikeUuid(String(nameFromCache))) {
      return String(nameFromCache)
    }

    const roomDisplay = candidate.displayName
    if (roomDisplay && !this._looksLikeUuid(String(roomDisplay))) {
      return String(roomDisplay)
    }

    const roomFirst = candidate.firstName
    if (roomFirst && !this._looksLikeUuid(String(roomFirst))) {
      return String(roomFirst)
    }

    if (nameFromCache) {
      return String(nameFromCache)
    }
    return chatToken || candidate.easyrtcid || 'Candidate'
  }

  _isFullCandidateInfo(info) {
    return Boolean(info?.id)
  }

  // Fetch candidate info (similar to legacy chat.js lines 390-402, 2406-2423)
  async fetchCandidateInfo(chatToken) {
    if (!chatToken || !this.coachChatCandidateInfoPath) {
      return null
    }

    // Only use cache when it has full API data (seeded room names are partial)
    const cached = this.candidateInfo[chatToken]
    if (cached && this._isFullCandidateInfo(cached)) {
      const candidateInfo = cached

      // Ensure candidate id is stored in chat slot data
      if (candidateInfo.id) {
        const chatSlots = ['chat-1', 'chat-2']
        for (const chatKey of chatSlots) {
          const slot = this.connectionState.chats?.[chatKey]
          if (
            slot?.chatHistory?.candidate_token === chatToken ||
            slot?.candidateId === chatToken
          ) {
            slot.candidateId = candidateInfo.id
            slot.candidateNumericId = candidateInfo.id
            break
          }
        }
      }

      this.notifyListeners('candidateInfoFetched', {
        chatToken,
        candidate: candidateInfo,
      })
      this._linkCandidateCacheToEasyrtcid(chatToken)
      this._refreshCandidatesWithCachedNames()
      return candidateInfo
    }

    try {
      const url = new URL(
        this.coachChatCandidateInfoPath,
        window.location.origin,
      )
      url.searchParams.set('chat_token', chatToken)

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.candidate) {
          // Merge with any partial room-seeded display name data
          this.candidateInfo[chatToken] = {
            ...this.candidateInfo[chatToken],
            ...data.candidate,
          }

          const candidateInfo = this.candidateInfo[chatToken]

          // Store candidate id in the chat slot data for easy access
          if (candidateInfo.id) {
            const chatSlots = ['chat-1', 'chat-2']
            for (const chatKey of chatSlots) {
              const slot = this.connectionState.chats?.[chatKey]
              if (
                slot?.chatHistory?.candidate_token === chatToken ||
                slot?.candidateId === chatToken
              ) {
                slot.candidateId = candidateInfo.id
                slot.candidateNumericId = candidateInfo.id
                break
              }
            }
          }

          this.notifyListeners('candidateInfoFetched', {
            chatToken,
            candidate: candidateInfo,
          })
          this._linkCandidateCacheToEasyrtcid(chatToken)
          this._refreshCandidatesWithCachedNames()
          return candidateInfo
        }
      }
    } catch (error) {
      console.error('CoachChatManager - Error fetching candidate info:', error)
    }

    return null
  }

  // Fetch candidate info for new candidates in room (similar to legacy chat.js lines 2406-2423)
  _fetchCandidateInfoForNewCandidates(candidates) {
    if (!candidates || !Array.isArray(candidates)) {
      return
    }

    // Check if we're in coach mode and have the candidate info path
    if (!this.coachChatCandidateInfoPath) {
      return
    }

    candidates.forEach((candidate) => {
      // Use candidate.username as chatToken (same as legacy chat.js line 2404, 2408)
      const chatToken = candidate.username

      if (
        chatToken &&
        (!this.candidateInfo[chatToken] ||
          !this._isFullCandidateInfo(this.candidateInfo[chatToken]))
      ) {
        // Fetch candidate info asynchronously
        this.fetchCandidateInfo(chatToken).catch((error) => {
          console.error(
            'CoachChatManager - Error fetching candidate info for',
            candidate.easyrtcid,
            error,
          )
        })
      }
    })
  }

  // Submit chat history to backend (similar to legacy chat.js lines 1427-1434, 3533-3546)
  // CTS-9775: returns a Promise and emits chatHistorySaved when the record is persisted
  // so Today's Chat History can refresh without waiting on a fixed delay / page reload.
  _submitChatHistory(chatInfo) {
    if (!this.coachEndChatPath) {
      console.warn(
        'CoachChatManager - coachEndChatPath not set, cannot submit chat history',
      )
      return Promise.resolve(null)
    }

    try {
      // Build form data similar to legacy implementation
      const formData = new FormData()
      formData.append('chat_info', JSON.stringify(chatInfo))
      formData.append(
        'coach_status',
        this.connectionState.availability || 'away',
      )

      return fetch(this.coachEndChatPath, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
        },
        body: formData,
      })
        .then(async (response) => {
          if (!response.ok) {
            console.error(
              'CoachChatManager - Failed to submit chat history:',
              response.status,
            )
            return null
          }

          let payload = null
          try {
            payload = await response.json()
          } catch (_err) {
            payload = { success: true }
          }

          this.notifyListeners('chatHistorySaved', {
            ...chatInfo,
            chat_history_id: payload?.chat_history_id,
            chat_type: payload?.chat_type || chatInfo.chat_type,
          })
          return payload
        })
        .catch((error) => {
          console.error(
            'CoachChatManager - Error submitting chat history:',
            error,
          )
          return null
        })
    } catch (error) {
      console.error('CoachChatManager - Error submitting chat history:', error)
      return Promise.resolve(null)
    }
  }

  // Helper function to format action messages into text strings
  formatActionMessageText(message) {
    const actionType = message.actionType || message.actionData?.action_type
    const actionData = message.actionData || {}

    // Special handling for chat_attachment
    if (actionType === 'chat_attachment') {
      const fileName =
        actionData.attachment_name || message.attachment?.name || 'file'
      return `sent ${fileName}`
    }

    // Handle other action types
    let actionText = ''
    switch (actionType) {
      case 'navigate':
        actionText = `navigated to ${
          actionData.action_target?.pathname || actionData.action_target || ''
        }`
        break
      case 'started_chat_from':
        actionText = `started chat from ${actionData.action_target || ''}`
        break
      case 'left_chat':
        actionText = 'Candidate has left the chat'
        break
      case 'pause':
        actionText = actionData.paused
          ? 'paused their camera'
          : 'unpaused their camera'
        break
      case 'mute':
        actionText = actionData.paused
          ? 'muted their audio'
          : 'unmuted their audio'
        break
      default:
        actionText = actionType || 'performed an action'
    }

    return actionText
  }

  /**
   * Resolve which chat slot a message belongs to (chat-1 or chat-2).
   * Avoids defaulting to slot 1 when metadata is missing during multi-chat.
   */
  _resolveMessageChatKey(message) {
    if (!message) return null
    if (message.chatKey) return message.chatKey
    if (message.chatSlot != null) return `chat-${message.chatSlot}`

    const chatId = message.chatId
    const peerId = message.fromEasyrtcid || message.senderId
    const activeChats = this.connectionState.activeChats || []

    let activeChat = null
    if (chatId != null) {
      activeChat = activeChats.find((chat) => chat.id === chatId)
    }
    if (!activeChat && peerId) {
      activeChat = activeChats.find(
        (chat) => chat.candidateEasyrtcid === peerId,
      )
    }
    if (activeChat) {
      return activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`
    }

    const selectedCandidates = [
      this.connectionState.selectedCandidate,
      this.connectionState.selectedCandidate2,
    ].filter(Boolean)

    for (const candidate of selectedCandidates) {
      if (chatId != null && candidate.id === chatId) {
        return candidate.chatKey || `chat-${candidate.chatSlot || 1}`
      }
      if (peerId && candidate.easyrtcid === peerId) {
        return candidate.chatKey || `chat-${candidate.chatSlot || 1}`
      }
    }

    return null
  }

  /**
   * Drop in-memory messages for one ended chat so other concurrent slots keep their thread.
   */
  _removeMessagesForEndedChat(activeChat) {
    if (!activeChat) return
    const chatId = activeChat.id
    const peerId = activeChat.candidateEasyrtcid
    const chatKey = activeChat.chatKey || `chat-${activeChat.chatSlot || 1}`

    this.connectionState.messages = (
      this.connectionState.messages || []
    ).filter((msg) => {
      const matchesEndedChat =
        (chatKey && msg.chatKey === chatKey) ||
        (chatId != null && msg.chatId === chatId) ||
        (peerId && (msg.fromEasyrtcid === peerId || msg.senderId === peerId))
      return !matchesEndedChat
    })
  }

  // Message management
  addMessage(message) {
    // Create a new array reference so React can detect the change
    this.connectionState.messages = [...this.connectionState.messages, message]

    // Format text for chat history
    let historyText = message.content || message.text || ''

    // If this is an action message, format it into text
    if (
      (message.type === 'candidate_action' ||
        message.type === 'coach_action') &&
      !historyText
    ) {
      historyText = this.formatActionMessageText(message)
    }

    // Also add to chat slot history if message has chatId or chatKey
    const isAttachment =
      message.actionType === 'chat_attachment' || message.attachment?.url
    const attachmentUrl =
      message.actionData?.action_target || message.attachment?.url
    const attachmentName =
      message.actionData?.attachment_name || message.attachment?.name

    const buildHistoryMessage = () => {
      const historyMessage = {
        text: historyText,
        sender: message.sender || 'coach',
        timestamp: message.timestamp || new Date().toISOString(),
      }
      if (isAttachment && attachmentUrl) {
        historyMessage.actionType = 'chat_attachment'
        historyMessage.attachment_url = attachmentUrl
        if (attachmentName) historyMessage.attachment_name = attachmentName
      }
      return historyMessage
    }

    const resolvedChatKey = this._resolveMessageChatKey(message)
    if (resolvedChatKey) {
      if (!message.chatKey) message.chatKey = resolvedChatKey
      if (message.chatSlot == null) {
        const slotMatch = resolvedChatKey.match(/^chat-(\d+)$/)
        if (slotMatch) message.chatSlot = Number(slotMatch[1])
      }

      const chatSlotData = this.connectionState.chats[resolvedChatKey]
      if (chatSlotData?.chatHistory) {
        chatSlotData.chatHistory.messages = [
          ...(chatSlotData.chatHistory.messages || []),
          buildHistoryMessage(),
        ]
      }
    }

    this.notifyListeners('messageAdded', message)
    this.notifyListeners('stateChange', this.getConnectionState())
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  notifyListeners(event, data) {
    // console.log(`CoachChatManager - notifyListeners called for event: ${event}`)
    // console.log(`CoachChatManager - notifyListeners data:`, data)
    /*
    console.log(
      `CoachChatManager - notifyListeners has ${this.listeners.size} total listeners`,
    )
    */

    if (this.listeners.has(event)) {
      const eventListeners = this.listeners.get(event)
      /*
      console.log(
        `CoachChatManager - notifyListeners found ${eventListeners.size} listeners for event: ${event}`,
      )
      */

      eventListeners.forEach((callback, _index) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    } else {
      /*
      console.log(
        `CoachChatManager - notifyListeners no listeners found for event: ${event}`,
      )
      */
    }
  }

  // Media device management
  async switchAudioDevice(deviceId) {
    return await this.mediaService.switchAudioDevice(deviceId)
  }

  async switchVideoDevice(deviceId) {
    return await this.mediaService.switchVideoDevice(deviceId)
  }

  getMediaStats() {
    return this.mediaService.getMediaStats()
  }

  getMediaState() {
    return this.mediaService.getMediaState()
  }

  getMediaDevices() {
    return this.mediaService.getMediaDevices()
  }

  // Handle candidate media state changes (pause/mute)
  handleCandidateMediaStateChange(data) {
    try {
      console.log(
        'CoachChatManager - handleCandidateMediaStateChange called:',
        data,
      )

      const { messageType, messageData, fromEasyrtcid } = data

      // Find the active chat with this candidate
      const activeChat = this.connectionState.activeChats.find(
        (chat) => chat.candidateEasyrtcid === fromEasyrtcid,
      )

      if (!activeChat) {
        // console.log(
        //   'CoachChatManager - No active chat found for candidate:',
        //   fromEasyrtcid,
        // )
        return
      }

      // Update the chat's media state
      if (messageType === 'video_paused' || messageType === 'video_unpaused') {
        activeChat.candidateVideoPaused = messageType === 'video_paused'
        // console.log(
        //   'CoachChatManager - Updated candidate video pause state:',
        //   activeChat.candidateVideoPaused,
        // )
      } else if (
        messageType === 'audio_muted' ||
        messageType === 'audio_unmuted'
      ) {
        activeChat.candidateAudioMuted = messageType === 'audio_muted'
        // console.log(
        //   'CoachChatManager - Updated candidate audio mute state:',
        //   activeChat.candidateAudioMuted,
        // )
      }

      // Notify listeners about the media state change
      this.notifyListeners('candidateMediaStateChanged', {
        chatId: activeChat.id,
        candidateEasyrtcid: fromEasyrtcid,
        messageType,
        messageData,
        activeChat,
      })

      // console.log(
      //   'CoachChatManager - Notified listeners of candidateMediaStateChanged',
      // )
    } catch (error) {
      console.error(
        'CoachChatManager - Error handling candidate media state change:',
        error,
      )
    }
  }

  cleanup() {
    this._releaseChatMedia()
    this.mediaService.cleanup()
  }
}

// Singleton instance for coach chat management
const coachChatManager = new CoachChatManager()
export default coachChatManager
